// Composing an EKRM out of channels taken from several models.
//
// The sibling of ekrmClone.js and deliberately not a variant of it. That module answers "copy THIS
// model": every section of the payload is read off one report, and everything the user can move is
// an edit against a copy. This one answers "build me a model with THESE channels", which is a
// different problem in exactly one place and the same problem everywhere else.
//
// The one place: there is no source model to read the whole-model sections off. No KPI history, no
// sales decomposition, no seasonality, no co-marketing spend. Those are GENERATED here from what
// the user states — a revenue, a year they drew — where the clone imports them.
//
// Everywhere else it is the same problem, so it is the same code. Every reader in ekrmClone.js that
// takes a `form` rather than a `scope` works unchanged over a composed form: the ROAS, the implied
// attribution, the base floor, the spend corridor, the trade-off curve, both solvers, the
// seasonality bounds and the whole curve editor. This module builds the form and nothing else.
//
// Nothing here can be reached from a mounted component: `vue` and `vue-template-compiler` are a
// patch apart in this project and @vue/test-utils refuses to load, so anything a test has to pin
// lives in a util and the components own no arithmetic at all.
import {
  CALENDAR_AVERAGE,
  CURVE_PRIORITY,
  band,
  completeCalendarYears,
  curveKnee,
  decayRange,
  displayName,
  importableChannels,
  kpiMonthly,
  monthlyDecayCurve,
  monthlyVectorTotal,
  monthsForYears,
  numericPairs,
  readChannel,
  scaleChannelCells,
  seasonalityVector,
  spendChannels,
  toCells
} from './ekrmFromModel'
import {
  createGrids,
  maxMonthlySpends,
  syncMediaGrids
} from './ekrmDefinition'
import { curveWithFittedTail, hillAt, hillDiminishingReturns, isHillParams } from './hillCurve'
import {
  LIMIT_MARGIN,
  MAX_ATTRIBUTION_SHARE,
  SHAPE_MONTH_CAP,
  SKIP_NO_CURVE,
  SKIP_NO_SATURATION,
  closeBase,
  cloneAttribution,
  cloneBaseMonthly,
  cloneSeasonalityPattern,
  cloneShapeCells,
  leastAttributionCentre,
  mean,
  positiveFactor,
  responseAt,
  seasonalityYear,
  sum,
  writeChannel,
  writeDerivedAttributions,
  writeSeasonalityBand
} from './ekrmClone'

// Every January averaged against every January. Not a choice the composer offers, and for a
// sharper reason than the clone has: a channel is being lifted out of its model's calendar and
// dropped into a model that has none, so "the real months of 2024" is a claim about a history this
// payload does not have. One typical year is the only honest window.
export const COMPOSE_MODE = CALENDAR_AVERAGE

// The grid runs out to where the response reaches its ceiling rather than to where the plan spends.
// Same reason as the clone: the curves travel as x/y and the ten columns are along for
// check_ekrm_correctness, so the question the grid answers is what the channel's response LOOKS
// like, not what band the plan will spend in.
export const COMPOSE_PRIORITY = CURVE_PRIORITY

// Twelve, always. There is no history to copy verbatim, so there is no period to pick.
export const COMPOSE_MONTHS = 12

// THE OPENING ATTRIBUTION, AND WHY THERE IS NO LONGER ONE. Kept as a note rather than a constant.
//
// A tenth used to PLACE the curves: every channel was laid out so that at a budget which was itself
// a tenth of the revenue, the media returned a tenth of it. That went first. A saturation curve is a
// property of the MEDIUM and of the market it is bought in — how much money Search absorbs in this
// country before it stops responding does not depend on how much of it anybody decides to buy — so
// the curves are stated outright, per channel, in dollars, and placed before there is a budget or a
// revenue on the page at all. See `statedCurves` on buildComposeForm.
//
// What survived that was a weaker job: OPENING A FIELD. The Total Revenue box was seeded with the
// figure that left the media explaining a tenth of it — composeOpeningRevenue — and tracked it until
// the user typed over it. That has gone too, and for the reason the first one did, one level up. It
// made the size of the business a reading of how much media had been drawn so far, and the size of
// the business is the figure the media are read AGAINST: cloneRevenueLimits mentions no spend at
// all, cloneSpendLimits reads every one of its ends against the revenue. An opening derived the
// other way round ran the wizard backwards, and it invited the user to accept a number nobody had
// decided.
//
// So the box opens empty and the money below it waits. Nothing is seeded in its place either: what
// appears the moment a revenue is typed is the plan the cards already describe, every channel at the
// saturation point its own card states, unmoved. Seating that plan on the low end of its spend
// corridor was tried and reverted — see the note at seatSpend in CreateModelCompose, which is where
// the corridor is read. See moneyReady there for the gate.

// THE LEAST ANY FIGURE IN THIS WIZARD MAY COME TO, in the payload's own money a month.
//
// Every number stated here travels into a grid cell, and a cell is a string that has been through
// toCell: two decimals under a thousand and whole units over it. So a figure below half a cent is
// not a small figure, it is the figure ZERO — and every rule validateEkrmForm holds the payload to
// is a statement about numbers that survived that rounding. A saturation point that rounded away is
// refused outright, a spend row summing to nothing is refused, a KPI row summing to nothing is
// refused, and a marketing attribution whose band no longer has a positive minimum is refused. Four
// errors in the wizard's own English, arriving over a screen where nothing looks wrong except that
// a figure reads 0.
//
// ONE WHOLE UNIT rather than the 0.005 where the cell dies, because this is not a rounding guard.
// It is what a placement MEANS: nobody states that a medium saturates at forty cents a month, and a
// rail standing a hair above the arithmetic is one the next rounding walks through. Everything that
// can move a figure is held above this — the two fields on the editor, the four grips on the zone
// bar, the three levers, the revenue and the media spend — so that no combination of them reaches a
// payload the build refuses. It is deliberately not scaled to anything: a floor read off the model
// being built would move as the model moved, which is exactly the ratchet these rails exist to
// stop.
export const COMPOSE_FLOOR = 1

// How far under a hundred the attribution minima are held, and how finely the wall is found. The
// validator errors AT a hundred and the rows it sums have each been through a rounding, so a band
// written to land exactly there is one a re-round can push over. A point of room is invisible in a
// figure the payload states to two decimals. See composeBandCeiling.
const BAND_SUM_LIMIT = 99
const BAND_PASSES = 40

// The least revenue whose year survives the row it is written into: twelve months, spread by the
// shape and each rounded into its own cell, and none of them under the floor.
//
// Read against the SHAPE and not against twelve equal months, because a shape is what decides which
// month is the smallest — a trough at 0.6 of the year's mean needs two thirds again as much revenue
// to keep its January above the floor as a flat year does. Null is not among the answers: a wizard
// asked for the least revenue always has one.
export function composeRevenueFloor (shape) {
  const spread = Array.isArray(shape) && shape.length === COMPOSE_MONTHS ? normaliseShape(shape) : null
  let least = 1
  if (spread) {
    for (let index = 0; index < spread.length; index++) {
      if (spread[index] > 0 && spread[index] < least) least = spread[index]
    }
  }
  return least > 0 ? COMPOSE_MONTHS * COMPOSE_FLOOR / least : COMPOSE_MONTHS * COMPOSE_FLOOR
}

// Whether a curve says anything this wizard may carry: where it saturates and what it generates
// there, both at or above the floor. The pair a card reads — see composeCurveReading — asked of
// parameters that have not been committed anywhere yet, which is what a drag on the zone bar and a
// lever mid-gesture both hold.
//
// It is the drag's half of the wall the levers get from cloneCurveLimits, which stops them at
// LIMIT_FLOOR off the same two figures. Two gestures, one rule, and it lives here rather than in the
// editor for the reason nothing in that component owns arithmetic: this is the thing a test can
// pin.
export function composeCurveAboveFloor (params) {
  if (!isHillParams(params)) return false
  const at = hillDiminishingReturns(params)
  return at >= COMPOSE_FLOOR && hillAt(params, at) >= COMPOSE_FLOOR
}

// The most revenue the marketing attribution survives, given what the curves are credited with.
//
// The attribution is what the media return over this figure, so the revenue is its DENOMINATOR:
// raising it drives the band down, and under leastAttributionCentre — about 0.065 % — `band` writes
// a minimum of nothing or less and validateEkrmForm refuses the payload. It is the one hard wall the
// revenue has, and it is a ceiling rather than a floor, which is the same wall the media spend
// stands against from the other side; see cloneSpendLimits, whose low end is this rule read as a
// spend.
//
// A hair inside it, for the reason every limit in this pair of wizards is: the band is written into
// two decimals of a cell and a limit standing exactly on the boundary is one a typed figure can land
// on and trip.
//
// Null when the media are credited with nothing at all — there is then no revenue that makes their
// share of it positive, and nothing to hold a typed figure against.
export function composeRevenueCeiling (marketing) {
  const total = Number(marketing)
  const least = leastAttributionCentre()
  if (!isFinite(total) || total <= 0 || !(least > 0)) return null
  return LIMIT_MARGIN * total * 100 / least
}

// THE SHAPE OF THE YEAR the revenue is spread across, as a name and as twelve multipliers.
//
// The size of the model is one figure, typed in the card these shapes stand in; how that figure
// falls across the twelve months is a different statement, and until now it was not one — the row was twelve equal
// months because a model built from scratch has no history to copy. Flat is still the default and
// still the honest opening, but it is now a CHOICE among others rather than the only thing the
// wizard can say, which closes the "flat KPI row against a declared seasonality" question: declaring
// that January runs above the year, against a row where January equals December, asks the model to
// explain shape that is not in the data.
//
// Every shape has a mean of one, so it redistributes the revenue without resizing it. The row is
// renormalised on the way in regardless — the total is the statement the user made and no shape may
// move it — and `Σ kpi = R` is what keeps the stated attribution exactly true, `cloneAttribution`
// being marketing over that sum.
//
// The troughs are deliberately shallow. Base revenue is a month's KPI less what the curves credit
// the media with, and the spend row is flat, so a month at 0.6 of the mean against an attribution of
// 10 % still leaves five sixths of itself as base. Drive the spend high enough and it will not, and
// that is answered where it already is: `baseWarning` and `decompositionBaseWarning` say so in
// words, and `revenueBars` draws a month with no base left as a column that is all media rather than
// as a column that has gone wrong.
export const COMPOSE_SHAPE_FLAT = 'flat'
export const COMPOSE_SHAPE_RISING = 'rising'
export const COMPOSE_SHAPE_FALLING = 'falling'
export const COMPOSE_SHAPE_BURSTS = 'bursts'
export const COMPOSE_SHAPE_RANDOM = 'random'

export const COMPOSE_REVENUE_SHAPES = [
  COMPOSE_SHAPE_FLAT,
  COMPOSE_SHAPE_RISING,
  COMPOSE_SHAPE_FALLING,
  COMPOSE_SHAPE_BURSTS,
  COMPOSE_SHAPE_RANDOM
]

// THE YEAR NOBODY CHOSE OFF A LIST, which is a different kind of answer from the five above and is
// why it is not among them. Those are patterns: a name, twelve multipliers generated from it, and a
// total that does not move — the shape redistributes the year and the figure typed over it says how
// big the year is. This one is the row itself, month by month, in the money the row is written in.
//
// So it is the one shape that RESIZES. A month typed in dollars is a statement about that month and
// about nothing else — a January that sold half a million sold half a million whatever December did
// — and the only honest thing the other eleven can do is stay exactly where they are, which leaves
// the total to move by the difference. That is the whole of the difference from the decay curve
// this control is otherwise a copy of: a decay's total is fixed, so a lag typed there is taken out
// of the lags around it; a year's total is not, so a month typed here is added to it.
//
// It is not in COMPOSE_REVENUE_SHAPES on purpose. That list is what the generator understands, and
// composeRevenueShape reads this name as it reads any other it does not know — as flat — which is
// the right answer for a caller asking it to GENERATE a row that is by definition not generated.
// What carries a custom year is the twelve multipliers beside the name, which is what
// composeRevenueShapeOf reads off the row and what every payload has carried all along.
export const COMPOSE_SHAPE_CUSTOM = 'custom'

// A row of money read back as a shape: twelve multipliers of mean one, which is the only form the
// rest of this module knows a year in. Null for anything that is not twelve positive months —
// the same null composeRevenueShape answers with, and composeKpiMonths spreads a null flat.
export function composeRevenueShapeOf (months) {
  if (!Array.isArray(months) || months.length !== COMPOSE_MONTHS) return null
  const values = months.map(function (value) { return Number(value) })
  for (let index = 0; index < values.length; index++) {
    if (!isFinite(values[index]) || values[index] < 0) return null
  }
  return normaliseShape(values)
}

// ONE MONTH TYPED, and the row it leaves behind. Every other month is handed back untouched — see
// above — so the year is resized by exactly the difference, and what comes back beside it is the
// wall the typed figure was held at, if it was held at one.
//
// The two walls are the revenue's own two, asked of a single month:
//
//   low   every figure in this wizard travels into a grid cell and a cell under the floor IS zero.
//         A KPI row with a month at nothing is one validateEkrmForm refuses, so a month typed under
//         COMPOSE_FLOOR lands on it. It is also what keeps composeRevenueFloor satisfied without
//         asking it: the floor is the revenue whose SMALLEST month clears the cell, and a row whose
//         every month clears it clears the row.
//   high  the year the months add up to is what the marketing attribution is read against, and past
//         composeRevenueCeiling that band rounds away to nothing. So the ceiling binds the SUM, and
//         a month is held at whatever is left of it once the other eleven have had their share.
//         Skipped where that leaves less than the floor — the ceiling is then under the floor, there
//         is no row that satisfies both, and holding a month at a wall it cannot meet says otherwise.
//
// The ceiling arrives as an argument rather than being read here: it is a function of what the
// curves return, which is a form this module builds rather than a row it is handed. Null for a
// caller with no ceiling to state.
export function composeRevenueMonthAt (months, index, amount, ceiling, floor) {
  const row = Array.isArray(months) ? months.map(function (value) { return Number(value) || 0 }) : []
  const typed = Number(amount)
  if (index < 0 || index >= row.length || !isFinite(typed)) {
    return { months: row, reason: null }
  }
  let held = typed
  let reason = null
  if (ceiling !== null && isFinite(ceiling)) {
    let others = 0
    for (let at = 0; at < row.length; at++) {
      if (at !== index) others += row[at]
    }
    const most = ceiling - others
    if (most >= COMPOSE_FLOOR && held > most) {
      held = most
      reason = 'high'
    }
  }
  // AND THE OTHER END OF THE SAME SUM. The year has to leave the base above its own floor once the
  // curves have been credited — see composeRevenueBaseFloor, which is what the caller passes — and
  // that binds the SUM exactly as the ceiling does, so a month is held at whatever is left of it
  // once the other eleven have had their share. Null for a caller with no such floor to state.
  //
  // It is checked after the ceiling and wins where the two disagree, which is the order the box
  // itself is held in: a plan whose ceiling is under its floor is one the media are wrong for, and
  // the base is the end that says so.
  if (floor !== null && floor !== undefined && isFinite(floor)) {
    let others = 0
    for (let at = 0; at < row.length; at++) {
      if (at !== index) others += row[at]
    }
    const least = floor - others
    if (least > held) {
      held = least
      reason = 'base'
    }
  }
  if (held < COMPOSE_FLOOR) {
    held = COMPOSE_FLOOR
    reason = 'low'
  }
  row[index] = held
  return { months: row, reason: reason }
}

// How far from the mean the shaped months are allowed to reach. One number for all of them so the
// choice between shapes is a choice of PATTERN and not of amplitude — a year that got louder as
// well as differently shaped would make the five options five levels as well as five shapes.
const SHAPE_SWING = 0.4

// Twelve multipliers, mean one. Null for flat and for a name nothing recognises, which is the same
// answer: nothing to spread by, and writeGeneratedGlobals then writes the twelve equal months it
// always wrote.
//
// `random` is the one that needs an argument. Its twelve numbers are drawn ONCE, by the caller, and
// then stored and passed back on every build — a shape re-rolled inside the build would redraw the
// year on every keystroke and nothing on the page would hold still. The source is injected so the
// tests can pin it; the wizard hands it Math.random.
export function composeRevenueShape (name, random) {
  const draw = typeof random === 'function' ? random : Math.random
  const months = []
  for (let index = 0; index < COMPOSE_MONTHS; index++) {
    const position = COMPOSE_MONTHS === 1 ? 0 : index / (COMPOSE_MONTHS - 1)
    switch (name) {
      case COMPOSE_SHAPE_RISING:
        months.push(1 - SHAPE_SWING + 2 * SHAPE_SWING * position)
        break
      case COMPOSE_SHAPE_FALLING:
        months.push(1 + SHAPE_SWING - 2 * SHAPE_SWING * position)
        break
      // One month on, two quiet, four times over. An alternating year reads as noise at twelve
      // columns wide; a third of the months carrying the weight reads as bursts, which is what it is.
      case COMPOSE_SHAPE_BURSTS:
        months.push(index % 3 === 0 ? 1 + 2 * SHAPE_SWING : 1 - SHAPE_SWING)
        break
      case COMPOSE_SHAPE_RANDOM:
        months.push(1 - SHAPE_SWING + 2 * SHAPE_SWING * draw())
        break
      default:
        return null
    }
  }
  return normaliseShape(months)
}

// Mean one, whatever came in. The shapes above are written to be that already and this is what makes
// it true rather than nearly true — `random` never is, and a caller may hand back a year read off a
// stored payload that has been through toCell.
function normaliseShape (months) {
  const total = sum(months)
  if (!isFinite(total) || total <= 0) return null
  return months.map(function (value) { return COMPOSE_MONTHS * value / total })
}

// The twelve KPI figures, in USD: the stated revenue spread by the shape, renormalised so the row
// sums to exactly what was stated. Null months where there is no revenue to spread.
export function composeKpiMonths (revenue, shape) {
  const total = Number(revenue)
  const monthly = isFinite(total) && total > 0 ? total / COMPOSE_MONTHS : 0
  const spread = Array.isArray(shape) && shape.length === COMPOSE_MONTHS ? normaliseShape(shape) : null
  const months = []
  for (let index = 0; index < COMPOSE_MONTHS; index++) {
    months.push(spread ? monthly * spread[index] : monthly)
  }
  return months
}

const REFERENCE_TIER = 'Reference 1'

// What the KPI row declares when no contributing model can say. Inside the [5, 40] every model's
// own figure is clamped into, so it can never be a value validateEkrmForm warns about.
const DEFAULT_VARIATION_RANGE = 20

// A model that cannot contribute a channel, and the two reasons that survive from the clone's
// three. NOT_READY_NO_ATTRIBUTION is gone on purpose: the composer writes its marketing band from
// the payload's own implied attribution, so it never reads attributionBands and never touches
// sales_decomposition_total. A model with no decomposition is a perfectly good channel source here
// and would have been refused by the clone.
export const NOT_READY_NO_KPI = 'noKpi'
export const NOT_READY_NO_CHANNELS = 'noChannels'

export function composeReadiness (scope) {
  const reasons = []
  const fit = scope && scope.v2 && scope.v2.model_fit && scope.v2.model_fit.Real
  if (!fit || !Array.isArray(fit.y) || sum(fit.y) <= 0) {
    reasons.push(NOT_READY_NO_KPI)
  }
  if (importableChannels(scope).length === 0) {
    reasons.push(NOT_READY_NO_CHANNELS)
  }
  return { ok: reasons.length === 0, reasons: reasons }
}

// WHERE THE SOURCE'S CURVE STOPPED PAYING: the spend at which that channel stopped responding, in
// the source model's own units, and what its curve returned there.
//
// The one thing read off a source that still carries a magnitude, and it is read only to be divided
// out. Normalising a channel's curve through this point leaves `(x/spend, y/response)`, which passes
// through (1,1) and is dimensionless — and what is left in it is the CURVATURE and nothing else.
// That is the whole of what this wizard imports; both levels are stated, in dollars, on the card.
//
// It used to be the mean of the channel's twelve monthly spends — where the channel RAN rather than
// where its curve turns — and moving it here is what lets the media be modelled before there is a
// budget or a revenue on the page. A mean monthly spend is a fact about how aggressively that
// business bought that medium; the knee is a fact about the curve. Only the second one is a property
// the user can state a counterpart to, and only the second one is a point they can be asked about:
// `curveKnee` is the platform's own "the spend at which the channel stops responding", which is
// exactly the sentence the card puts a field under. It also closes what the doc listed as an open
// question — a flighted channel's mean is a figure it never actually spent, and on a convex stretch
// the response at the mean is not the mean of the responses.
//
// curveKnee and deliberately NOT read.knee, which is a different quantity wearing a similar name.
// Under CURVE_PRIORITY readChannel runs its knee through curveGridPoint, out to where the fitted
// Hill reaches its ceiling, because the ten stored grid values are the only place the backend's
// refit can read that ceiling from. That point is an order of magnitude past anything the source
// ever spent — for a fixture spending two thousand a month it lands near a hundred and ten thousand
// — and normalising through it would map the whole measured curve into the first tenth of the plan's
// first dollar, leaving every level the user states sitting on the flat tail where responseAt has
// nothing left to interpolate. What is wanted is the boundary between the optimal zone and
// over-investment: the spend at which the response reaches nine tenths of the ceiling, which is the
// report's own fourth region.
//
// It is SOLVED off the fit and not read off the source's regions array, and the difference is the
// difference this whole wizard is about. The backend measures its quantiles against the highest
// response its INPUT RANGE reached rather than against the ceiling, so a business whose range
// stopped at four fifths of its own curve has its ninetieth percentile marked far short of where the
// curve actually turns — and normalising through that imports how much of its own curve that
// business happened to explore, which is the same class of borrowed contingency as its budget.
// hillDiminishingReturns is a property of k and n and of nothing else; it is what the EKRM being
// built will itself report, its grid being laid out to reach CEILING_TARGET of the ceiling so that
// the refit's own range does cover the curve; and it is the quantity the editor shades the fifth
// zone from, which is what makes the stated saturation point and that band's edge the same place
// under every lever rather than a per-channel ratio apart.
//
// A channel whose fit was REJECTED keeps curveKnee. There the three parameters do not describe the
// curve, so a boundary solved off them would not describe it either, and the source model's own
// answer is the honest one left.
//
// read.knee still travels, and still lands past the stated figure: scaleChannelCells multiplies it
// by the same scale, so the payload's Saturation Point cell goes on marking where the grid has to
// reach rather than where the medium stops paying. The two are a constant ratio apart and both are
// wanted.
//
// The response is still read with responseAt over the curve's own points, and the reason is
// unchanged and is not cosmetic: mediumResponseCurve prefers form.curves[medium].source.saturation,
// which for an unedited channel is curvePayload of these very points scaled, and responseAt is
// homogeneous — so the payload curve read at the stated saturation point returns EXACTLY the stated
// revenue, chords and all. Reading it off the fit instead would be right only where the polyline
// happens to agree with the fit, and the two disagree by a chord everywhere between two points.
//
// What that requires is that the polyline describe the curve AT the anchor, and a solved boundary
// routinely lands past the last spend the source model ever explored — where responseAt reads a
// curve as flat and the reading would be several percent low. composeImport puts the fit's own tail
// back on the curve for exactly this reason, with the anchor itself among its points, so at the one
// spend this arithmetic turns on the polyline carries the curve's own value rather than a chord.
//
// Null, never a guess, when there is nothing to divide by: no knee, fewer than the two pairs
// curvePayload itself requires, or a curve that returns nothing where it is supposed to have
// stopped paying.
export function composeAnchor (read) {
  if (!read) return null
  const spend = read.usable && isHillParams(read.fit)
    ? hillDiminishingReturns(read.fit)
    : curveKnee(read.curve, read.sourceSpend)
  if (!isFinite(spend) || spend <= 0) return null
  const pairs = numericPairs(read.curve)
  if (pairs.length < 2) return null
  const response = responseAt({
    x: pairs.map(function (pair) { return pair[0] }),
    y: pairs.map(function (pair) { return pair[1] })
  }, spend)
  if (!isFinite(response) || response <= 0) return null
  return { spend: spend, response: response }
}

// One model's importable channels, read and sifted, with nothing built yet.
//
// The expensive half — a Levenberg-Marquardt fit per channel — and the composer caches it per
// model. It depends on the report and on nothing else at all: not on a unit rate, not on the
// revenue, not on a level. That is what readChannel's existence buys, and it is what lets the
// revenue be a resize of every curve without re-fitting anything.
//
// The two sift rules are the clone's and are asked at a probe scaling of one, because both are
// scale-invariant for a positive factor. The second is the one that is not about the channel being
// poor: NgEKRM.saturation_curves_complete requires EVERY medium of reference.media to supply an
// x/y, and api.py reads it as all-or-nothing, so one medium without a curve sends every other
// medium down the expert-grid path. Under COMPOSE_PRIORITY that path is ruinous — the grid is laid
// out dozens of knees out, the refit puts the knee a hundred times above the plan, and the built
// model attributes essentially nothing to marketing.
//
// WHAT IS NOT SIFTED, and the clone's third rule dropped: a channel that spent nothing. The clone
// refuses one because its Media Spend row IS the source's row and a medium there to say it was not
// bought is a row of zeros nobody asked for. Here the row is stated rather than imported — a picked
// channel opens standing at its own saturation point, and a copied one carries the source's year as
// a MULTIPLIER over that point — so a channel with no spend is not a channel with nothing to say:
// its curve, its knee and its decay are as measured as any other's. That is a medium the user can
// carry and then buy, which is exactly what somebody copying a model with a dormant channel in it is
// after; refusing it lost the curve too. Its spend row comes across as the twelve zeros it was.
// The curve a composed channel travels with: its own points, plus the fit's tail past the last of
// them, plus the point its placement is defined through. See curveWithFittedTail for why, and
// composeAnchor for what depends on it.
//
// Here and not in readChannel, which the clone shares: a clone reproduces one model at its own size,
// where the plan spends where that business spent and the tail is never reached. A composed channel
// opens standing at its SATURATION POINT, which is past the end of a source curve whenever that
// source's own range stopped short of its ceiling — so for this wizard the tail is not an edge case.
//
// measuredSpan is kept because the card clips its sparkline to it: what the source measured is still
// what the thumbnail should draw, and a hundred pixels of fitted tail would squeeze every shape
// against the axis.
function extendReadCurve (read) {
  if (!read || !read.usable || !isHillParams(read.fit)) return read
  const pairs = numericPairs(read.curve)
  const measuredSpan = pairs.length > 0 ? pairs[pairs.length - 1][0] : 0
  const curve = curveWithFittedTail(read.fit, read.curve, hillDiminishingReturns(read.fit))
  return Object.assign({}, read, { curve: curve, measuredSpan: measuredSpan })
}

export function composeImport (scope, options) {
  const opts = options || {}
  const config = (opts.model && opts.model.reference_model && opts.model.reference_model.config) || {}
  const readOptions = {
    mode: COMPOSE_MODE,
    months: null,
    granularity: config.decay_granularity ? config.decay_granularity : 'M',
    saturationGranularity: config.saturation_granularity ? config.saturation_granularity : 'M',
    priority: COMPOSE_PRIORITY
  }

  const kept = []
  const skipped = []
  importableChannels(scope).forEach(function (raw) {
    const read = extendReadCurve(readChannel(scope, raw, readOptions))
    const probe = scaleChannelCells(read, { scale: 1, revenueScale: 1 })
    if (!(Number(probe.saturationPointCell) > 0)) {
      skipped.push({ raw: raw, reason: SKIP_NO_SATURATION })
      return
    }
    if (!probe.saturationCurve) {
      skipped.push({ raw: raw, reason: SKIP_NO_CURVE })
      return
    }
    kept.push({
      raw: raw,
      read: read,
      // What this channel spends in a typical month, in the SOURCE's own units. The picker shows it
      // so a channel can be recognised before it is added; nothing in the build reads it.
      monthlySpend: mean(probe.spendCells)
    })
  })

  return { kept: kept, skipped: skipped }
}

// A CHANNEL NOBODY IMPORTED: the same reading composeImport produces per channel, generated instead
// of read.
//
// The wizard's other way in takes a medium out of a model that measured one, and everything below
// hangs off what readChannel returned for it — the fit, the curve, the knee the placement is
// normalised through, the decay. A channel invented here has no such measurement and needs the same
// structure anyway, so it is written out in full: a curve with a plain S in it, a decay that peaks
// on impact and is half gone inside a month, and a knee to normalise through. Nothing downstream
// asks where a read came from, which is why this is the whole of what "from scratch" costs.
//
// WHAT THIS IS AND WHAT IT IS NOT. It is not a claim about the channel — nobody has measured it and
// this file cannot pretend otherwise. It is an opening position, and it is the same one for every
// scratch channel, so what the user states afterwards is measured against a shape they can see: the
// two figures on the card place it in dollars, the levers in the editor bend it, and the tiles under
// them redraw the decay. The provenance says the model never claimed any of it — see the composer's
// own `scratch` flag.
//
// The parameters are unit-sized on purpose. Every curve on this page is placed by division through
// its own anchor and multiplication by the stated pair, so a level here would be divided straight
// back out; what survives the placement is the SHAPE, which is the exponent and nothing else. A
// mild S rather than a hyperbola because a medium with no threshold at all is the rarer claim, and
// because the exponent is what the editor's first lever moves.
const SCRATCH_HILL = { ok: true, vmax: 1, k: 1, n: 1.5, rmsePct: 0 }

// The ladder the curve is drawn on, in knees: far enough below the knee to carry the toe of the S
// and far enough past it to carry the flat, geometric so the points fall where the curve is moving.
// The knee itself is put on the ladder as a point of its own for curveWithFittedTail's own reason —
// composeAnchor reads the response THERE, and a polyline returns a chord anywhere between two
// points, so the one reading the whole placement turns on has to be the curve's own value.
const SCRATCH_CURVE_FROM = 1 / 40
const SCRATCH_CURVE_TO = 3
const SCRATCH_CURVE_POINTS = 24

// Where a scratch channel's response peaks and how long it takes to have delivered half of itself,
// in months. The fastest shape the platform reads: everything lands in the month the money is spent
// and the tail is nearly gone by the next one. It is the least a decay can claim, which is the right
// opening for a channel nobody has measured — a wear-in or a long tail is something the user knows
// about their own medium and says on the tiles.
const SCRATCH_DECAY_PEAK = 0
const SCRATCH_DECAY_HALF_LIFE = 1

export function composeScratchRead () {
  const fit = Object.assign({}, SCRATCH_HILL)
  const knee = hillDiminishingReturns(fit)

  const x = []
  for (let step = 0; step < SCRATCH_CURVE_POINTS; step++) {
    const ratio = SCRATCH_CURVE_FROM * Math.pow(
      SCRATCH_CURVE_TO / SCRATCH_CURVE_FROM, step / (SCRATCH_CURVE_POINTS - 1)
    )
    x.push(knee * ratio)
  }
  x.push(knee)
  x.sort(function (a, b) { return a - b })
  const curve = { x: x, y: x.map(function (at) { return hillAt(fit, at) }) }

  const decay = composeDecayCurve(
    composeDecayTemplate(SCRATCH_DECAY_PEAK, SCRATCH_DECAY_HALF_LIFE, DECAY_SPAN_MONTHS, 1)
  )

  return {
    hasSpend: true,
    hasSaturation: true,
    curve: curve,
    // Twelve months at the knee. Nothing built reads them — composeCells states the row itself, from
    // the saturation point on the card, and overrules the variation range this vector would set —
    // but the field is part of the shape a read has and a reader of one should not find it empty.
    sourceSpend: new Array(COMPOSE_MONTHS).fill(knee),
    sourceDecay: decay,
    fit: fit,
    usable: true,
    knee: knee,
    decay: decayRange(decay, 'M'),
    decayCurve: decay,
    // What the card clips its sparkline to. The whole curve here IS the drawing, there being no
    // measurement that stopped anywhere, so the span is its last point.
    measuredSpan: x[x.length - 1]
  }
}

// The medium name a channel takes when it is added, and the reason channelLabels cannot be used
// here.
//
// That function names a whole model's channels at once and dedupes POSITIONALLY over one sorted
// list, which is exactly right when the list is a model's own and is fixed. Here the list is the
// order the user added things in, and it changes: naming it afresh on every add would hand a freed
// name to a channel below and rename a medium nobody touched. The medium name keys three maps of
// the definition — media_spend.own_controllable, decay and saturation — so two channels that end up
// sharing one do not merely look alike, the second overwrites the first and the payload is built one
// channel short, silently, with the lost channel's spend still in every total.
//
// So a name is claimed ONCE, at the moment the channel is added, against the names already taken,
// and then it is stored on the entry and never recomputed. A collision is qualified by the model it
// came from, which is the useful thing to say when two models both call something Search, and only
// then falls back to a number.
export function composerLabel (model, raw, taken) {
  const used = taken || []
  const free = function (name) {
    return !!name && used.indexOf(name) === -1
  }
  const base = displayName(model, raw) || String(raw).trim() || 'Channel'
  if (free(base)) return base

  const modelName = String((model && model.name) || '').replace(/\s+/g, ' ').trim()
  if (modelName) {
    const qualified = base + ' (' + modelName + ')'
    if (free(qualified)) return qualified
  }

  for (let suffix = 2; suffix < used.length + 3; suffix++) {
    const numbered = base + ' (' + suffix + ')'
    if (free(numbered)) return numbered
  }
  return base
}

// One channel's cells at the size the user gave it. The anchor and the two stated figures meet here
// and nowhere else, and they meet as a division because that is all the import is: the source's
// curve is normalised through the point where it stopped responding, and then re-dimensioned onto
// the point where the user says THIS medium stops responding.
//
//   scale        = knee    / anchor.spend
//   revenueScale = ceiling / anchor.response
//
// So the source point (anchor.spend, anchor.response) lands exactly on (knee, ceiling), and every
// other point of the curve follows it by the same two factors. What survives is the CURVATURE and
// nothing else — how sharply the response turns on the way to its knee. What does not survive is the
// level of either axis, nor the headroom: how big the business a channel was fitted inside happened
// to be, what its units were worth, and how aggressively that business happened to buy that medium
// are facts about that business rather than about the medium.
//
// `knee` reaches the spend row as well as the x axis, and it is not multiplied on the way: the row
// is STATED rather than imported and arrives already in USD, so the channel opens standing exactly
// at the saturation point it was given. Only the curve's shape and the decay are the source's.
export function composeCells (entry, knee, ceiling, anchor) {
  const point = anchor || composeAnchor(entry.read)
  if (point === null) return null
  if (!isFinite(knee) || knee <= 0) return null
  if (!isFinite(ceiling) || ceiling <= 0) return null
  const cells = scaleChannelCells(entry.read, {
    scale: knee / point.spend,
    revenueScale: ceiling / point.response,
    monthlySpend: knee,
    // The fallback if there is no stated spend to write: a model built from a budget states how much
    // it spends, not a calendar it spends it on, and a channel's own year belongs to the model it was
    // lifted out of.
    flatten: true
  })
  // AND THE ONE FIGURE scaleChannelCells STILL TAKES FROM THE SOURCE, overruled here.
  //
  // The variation range is not part of what a row says — it is how far the fitter may move it — and
  // scaleChannelCells measures it on the source's OWN twelve months rather than on the cells it
  // returns, deliberately: it flattens a row when the caller states a monthly figure, and a flat
  // row's own dispersion is zero, which clamps to the floor and declares a confidence about the
  // monthly split nobody has. For the clone wizard that is the right answer, the dispersion being a
  // fact about the same business whose year is being reproduced.
  //
  // Here it is not. This row is twelve copies of a figure the user typed on a card, and what rode
  // beside it was the coefficient of variation of how some OTHER business happened to phase its
  // buying of this medium — a coefficient of variation being scale-free, the placement above does
  // not even dilute it, so it arrived verbatim. It is the same kind of claim as the KPI row's own
  // range, which is the platform's default for the same reason: see writeGeneratedGlobals.
  //
  // So the composer declares that default too. Nobody has measured this row's phasing — there is
  // nothing to measure, it opens flat — and the honest statement is the middle of the band rather
  // than either end of it: the floor would tell the fitter the split is exact, the ceiling would
  // hand it the whole row.
  return Object.assign({}, cells, { spendVariationRange: String(DEFAULT_VARIATION_RANGE) })
}

// ONE CHANNEL'S YEAR, SHAPED. composeCells writes twelve copies of the saturation point — a channel
// opens spending the same money every month, which is the only opening a card stating one figure can
// honestly produce — and this is where a month typed on the chart lands.
//
// A multiplier per column rather than an amount, for the reason every other edit on this page is a
// ratio: the spend level and the mix are moved by factors above this, and a stated cell would be a
// figure that stopped following them. The array is not renormalised — see `spendShapes` — so the
// channel's own total moves by the difference and the year's total with it.
//
// The cells are returned as they arrived wherever nothing has been typed: no entry, an empty array,
// or a column whose multiplier is not a positive number. A composer with no shapes at all writes the
// object composeCells returned, unchanged and identical.
function shapeSpendCells (cells, shape) {
  if (!cells || !Array.isArray(shape) || shape.length === 0) return cells
  const row = cells.spendCells || []
  return Object.assign({}, cells, {
    spendCells: row.map(function (cell, month) {
      const factor = Number(shape[month])
      // Zero is a month, not a missing multiplier: a channel that buys nothing in July is a plan
      // somebody can state, and the floor that matters is the medium's own largest month rather than
      // any one of them. Anything unreadable or negative leaves the cell where it was.
      if (!isFinite(factor) || factor < 0) return cell
      return (Number(cell) || 0) * factor
    })
  })
}

// WHAT THE TWO FIGURES ON A CARD READ once the channel's curve has been moved.
//
// The pair the user typed is where the curve was PLACED, and the three levers in the editor move the
// curve off it: drag the knee outward and the medium saturates somewhere else, raise the ceiling and
// it generates more there. Left alone, the card would go on printing the placement and describing a
// curve that no longer exists — the exact defect that took the whole-model attribution field off this
// page, arriving one level down.
//
// So the figures are readings, and what they read is WHERE THE CURVE SATURATES NOW and what it
// makes there — solved off the curve's own three parameters:
//
//   knee    = hillDiminishingReturns of the curve as it currently stands.
//   ceiling = that curve read at it, which is nine tenths of its ceiling.
//
// One definition, and the same one the editor's red dot is drawn at and the fifth optimal zone
// begins at, so the three cannot drift apart under any lever. It used to be the stated knee
// multiplied by the k lever, with the response looked up on the payload curve. That was right for
// two of the three levers and silently wrong for the third: an EXPONENT moved changes where a curve
// saturates, and a stated figure times the knee lever cannot say so. It also read the response as a
// chord between whichever two points the payload happened to carry — which for an edited channel is
// a curve generated from these very parameters, so it was sampling a function and reading the
// samples back.
//
// At rest both come back to the typed pair to floating point: no edit means k rides at 1, and the
// curve passing through (knee, ceiling) is what composeCells is for. So a card nobody has edited
// shows what was typed into it, which is the one thing this must not get wrong.
//
// Null, never a partial answer, for a channel that has not been stated: there is no placement to
// report the movement of.
export function composeCurveReading (stated, curveEdit, hill) {
  const knee = Number(stated && stated.knee)
  const ceiling = Number(stated && stated.ceiling)
  if (!isFinite(knee) || knee <= 0) return null
  if (!isFinite(ceiling) || ceiling <= 0) return null
  const at = isHillParams(hill) ? hillDiminishingReturns(hill) : 0
  if (at > 0) return { knee: at, ceiling: hillAt(hill, at) }
  return {
    knee: knee * positiveFactor(curveEdit && curveEdit.k, 1),
    // The fallback is the two levers alone, for the one case the parameters cannot answer: a channel
    // whose curve could not be built at all. It is what the reading would come to if the knee had
    // simply been stretched and the ceiling simply raised.
    ceiling: ceiling * positiveFactor(curveEdit && curveEdit.vmax, 1)
  }
}

// The placement a typed figure comes back to, which is the same conversion run backwards.
//
// The card shows the reading, so the box has to take the reading back — the contract every other box
// on this page keeps, and the reason Media Spend applies a ratio rather than writing a level. Typing
// 200 000 into a card whose knee lever stands at 1.5 must leave the card reading 200 000, so what is
// stored is the placement that reads as it.
//
// The two are independent and stay independent. Moving the placement on x re-scales the curve
// horizontally, and the response at the moved point rides along unchanged; moving it on y is a
// linear scale of the whole response, so the reading follows it by exactly the same ratio. Neither
// arithmetic needs to know which levers were moved or by how much.
//
// Null when there is nothing to convert against — an unstated channel, an unreadable figure, or a
// key that is not one of the two — and the caller then writes the typed figure as the placement,
// which for a channel with no curve yet is the same number either way.
export function composeRestate (stated, curveEdit, curve, key, value) {
  const reading = composeCurveReading(stated, curveEdit, curve)
  const typed = Number(value)
  if (reading === null || !isFinite(typed) || typed <= 0) return null
  if (key === 'knee') {
    if (!(reading.knee > 0)) return null
    return { knee: stated.knee * typed / reading.knee, ceiling: stated.ceiling }
  }
  if (key === 'ceiling') {
    if (!(reading.ceiling > 0)) return null
    return { knee: stated.knee, ceiling: stated.ceiling * typed / reading.ceiling }
  }
  return null
}

// ---------------------------------------------------------------------------
// The other curve a channel arrives with
// ---------------------------------------------------------------------------
//
// A CHANNEL IS TWO CURVES AND ONLY ONE OF THEM COULD BE MOVED. The saturation half is placed by the
// pair on the card and reshaped by the levers under the chart; the decay half — how a month's spend
// on this medium is delivered over the months that follow it — arrived from the source model and
// stood there.
//
// Standing is the right OPENING and it is still the opening: the source model measured this, off a
// spend history and a sales history, which is the kind of claim this wizard has no other way of
// making. What it cannot be is the last word. The same medium bought in another market, on another
// creative rotation, or by a business whose customers take a month longer to decide is a medium
// that carries differently from the model that fitted it — and that is a statement about the
// CHANNEL, exactly as its saturation point is. Which is why the control for it neither waits behind
// the expert switch nor waits for the pair to be typed: nothing about how a channel delivers is a
// reading of how much money it can absorb, and the kernel below is true before anybody has said
// where the medium saturates.
//
// WHAT IS EDITED IS THE KERNEL ITSELF. The payload can carry a decay two ways — as two brackets,
// months to maximum effect and months to half-life, and as the curve the report measured — and the
// builder reads the curve in preference to the brackets wherever there is one. So the curve is what
// this wizard hands the user: a bar per lag month, dragged. The brackets still travel, because
// check_ekrm_correctness reads months_to_half_life straight off the raw document and throws on one
// that carries only a curve, but they are DERIVED from the drawn kernel by the same decayRange the
// import derives them with. The two accounts of a channel's decay cannot disagree, because there is
// only one of them and the second is a reading of it.

// THE MASS IS THE ONE THING A DRAG MAY NOT MOVE.
//
// A decay kernel spreads one month's spend across the months that follow it. Its total is how much
// that spend is worth in the end — which is the saturation point stated three inches away on the
// same screen — and its SHAPE is when the money arrives, which is the only thing this control is
// about. A drag that moved the total would silently restate what the channel returns while the two
// figures that state it stood unchanged, and there would be no way to tell from the screen which of
// the two was true.
//
// So the drag never touches a weight. It moves the two figures the shape is generated FROM — see
// composeDecayFromShape — and the kernel is rebuilt at the total it already had, which is the only
// way of promising both that the level cannot move and that what is left is still a decay. The
// total is the SOURCE's own and is never normalised to
// one: nothing in this codebase reads these curves as probabilities — decayRange divides by the
// total it finds, decayBars normalises against the peak — and a kernel rescaled on the way in would
// send a channel's decay in different units from the ones its model reported it in.

// The lag months the editor lays out. Eight, and it is a span rather than a headroom: the slowest
// shape on offer has half of the response still to arrive at month three, and a kernel laid out over
// four months cannot hold one — the tail would be cut off by the grid rather than by the channel,
// and a tile would be filed under a figure its own curve could not reach. Eight is where every
// combination fits with room past it.
//
// Never fewer than the source measured either, which is the other half of the rule: a kernel
// truncated on the way to the screen would lose mass nobody moved anywhere. A source with thirteen
// monthly lags gets thirteen columns.
const DECAY_SPAN_MONTHS = 8

function decayMonths (measured) {
  return Math.max(measured, DECAY_SPAN_MONTHS)
}

// THE KERNEL A CHANNEL OPENS ON: the decay its own model measured, on the monthly lag axis the EKRM
// being built will read it on, padded out to the months the editor draws.
//
// The re-binning is monthlyDecayCurve's and is the same one the card's bars go through. It used to
// be a conversion of the PICTURE only — a weekly model's kernel was drawn on months and sent on
// nothing, the monthly kernel being the weekly one convolved with the within-month spend spread and
// a re-binning not being that convolution. That objection is about sending a re-binning as the
// MODEL's measurement, and it still holds: an untouched weekly channel sends no curve, exactly as
// before. What a user draws on top of it is their own claim about months and travels as one.
//
// Null where there is no decay to open on: no kernel, a kernel with an unreadable or negative
// weight in it, or one that is flat at zero. The editor says so rather than offering bars that
// cannot be dragged anywhere, a kernel with no mass having nothing for a drag to redistribute.
export function composeDecayKernel (curve, granularity) {
  const monthly = monthlyDecayCurve(curve, granularity === 'W' ? 'W' : 'M')
  const measured = monthly && Array.isArray(monthly.y) ? monthly.y.map(Number) : []
  if (measured.length === 0) return null
  if (measured.some(function (value) { return !isFinite(value) || value < 0 })) return null
  if (!(sum(measured) > 0)) return null

  const months = decayMonths(measured.length)
  const kernel = []
  for (let lag = 0; lag < months; lag++) {
    kernel.push(lag < measured.length ? measured[lag] : 0)
  }
  return kernel
}

// The kernel as the payload carries it: x is the lag in months and y the weight there.
//
// Trailing zeros are cut, and at least two lags are kept whatever happens. The cut is not tidiness:
// a run of zeros on the end is a claim that the response was measured out to month seven and found
// to be nothing there, where the truth is that the editor drew seven bars and the user used four.
// The floor of two is curvePayload's own — a curve of one point is not a curve — and it is also the
// least a decay can be and still say anything: one month of delivery and one of none.
export function composeDecayCurve (values) {
  const kernel = (values || []).map(Number)
  if (kernel.length === 0 || kernel.some(function (value) { return !isFinite(value) || value < 0 })) return null
  if (!(sum(kernel) > 0)) return null
  let last = kernel.length - 1
  while (last > 1 && !(kernel[last] > 0)) last--
  const drawn = kernel.slice(0, last + 1)
  return {
    x: drawn.map(function (value, lag) { return lag }),
    y: drawn
  }
}

// The two brackets the payload states beside the curve, READ OFF the curve rather than typed. Off
// decayRange, which is the same function the import reads them off a report's kernel with — so a
// channel whose kernel was redrawn arrives at the builder with a bracket that means the same thing
// the drawing does, and a channel left alone is bit-for-bit what it always was.
//
// The granularity is always 'M': what is drawn here is monthly whatever the source was fitted on.
export function composeDecayRanges (values) {
  const curve = composeDecayCurve(values)
  return curve === null ? null : decayRange(curve, 'M')
}

// Everything one edited channel hands writeChannel: the two brackets and the kernel they were read
// off. Null for a kernel nothing can be built from, which leaves the channel on its source's own
// decay rather than on a broken version of it.
export function composeDecayPayload (values) {
  const curve = composeDecayCurve(values)
  if (curve === null) return null
  return { ranges: decayRange(curve, 'M'), curve: curve }
}

// THE SHAPES A DECAY CAN BE, AND WHY THEY ARE A LIST RATHER THAN A GESTURE.
//
// A kernel drawn bar by bar is a control that asks the user to be an arithmetic they have no reason
// to hold: every bar moved moves all the others, because the total is fixed, so a shape is reached
// by pushing at it until it looks right rather than by saying what it is. And there is nothing to
// say with the freedom it buys — a decay is not an arbitrary curve, it is a response that climbs to
// a peak and falls away, and the whole of what anybody knows about one is WHERE it peaks and HOW
// FAST it falls. Those are the two figures the payload has always stated it in.
//
// So the two figures come back as the axes of a LIST. Every combination of them that a decay can
// actually be is generated as a kernel, drawn small, and picked; the two numbers are what the tiles
// are arranged by rather than what is typed into. The user reads shapes and the payload gets a
// curve, which is the thing this wizard could not do while the control was two sliders.

// Where the response may peak, in months after the spend. The platform's own range for this figure
// — decayRange clamps every reading into it, and the expert wizard draws its slider across it.
const DECAY_PEAKS = [0, 1, 2]

// And how long it may take to have delivered half of itself, per peak. Not one list for all three,
// because the two figures are not independent and the dependence is not a rule anybody chose: a
// response that is still climbing through month one cannot have delivered half of itself inside
// month zero. Each row begins at the fastest decay its own peak can have — the shape with nothing
// left in the tail at all — and runs out to the three months decayRange stops reading at.
//
// Four to a row, and the same four questions asked of each peak: the fastest this peak can decay,
// the ceiling the platform reads up to, and two steps between them. Rows of three and four were an
// accident of which figures looked round; a grid whose rows are different lengths reads as three
// unrelated lists rather than as one question asked three times.
const DECAY_HALF_LIVES = {
  0: [0.5, 1, 2, 3],
  1: [1.5, 2, 2.5, 3],
  2: [2, 2.25, 2.5, 3]
}

// How the tail falls. Bisected rather than solved because the crossing is interpolated inside the
// month it falls in — see shapeHalfLife, which is decayRange's own reading — and a closed form for
// it would be a second definition of the same thing. Forty halvings put the rate inside 1e-12 of
// the one that lands the crossing exactly where the tile says it does.
const DECAY_RATE_STEPS = 40
const DECAY_RATE_LOW = 1e-9
const DECAY_RATE_HIGH = 0.999999

// One shape at one rate, summing to one: a linear climb to the peak and a geometric fall away from
// it. The climb is what "months to maximum effect" MEANS — a channel with a wear-in period is still
// working in the months before it peaks — and a flat run of zeros before the peak would be a delay
// rather than a wear-in.
function decayShape (peak, rate, months) {
  const weights = []
  for (let lag = 0; lag < months; lag++) {
    weights.push(lag < peak ? (lag + 1) / (peak + 1) : Math.pow(rate, lag - peak))
  }
  const total = sum(weights)
  return weights.map(function (weight) { return weight / total })
}

// Where a unit-mass shape has delivered half of itself, interpolated inside the month it happens in.
// The same reading decayRange takes off a report's kernel, which is what makes a tile's caption true
// of the payload it produces: the figure the builder is handed is the figure the tile is filed under.
function shapeHalfLife (weights) {
  let running = 0
  for (let lag = 0; lag < weights.length; lag++) {
    if (running + weights[lag] >= 0.5) {
      return lag + (weights[lag] > 0 ? (0.5 - running) / weights[lag] : 0)
    }
    running += weights[lag]
  }
  return weights.length - 1
}

// ONE TEMPLATE: the kernel that peaks where it is asked to and has delivered half of itself when it
// is asked to, at the total the channel is already carrying.
//
// `total` is the source model's own and is never one. It is the whole point of taking it as an
// argument: picking a shape may not move what a month's spend is worth in the end — that is the
// saturation point, stated by the pair on the same card — so a template is a SHAPE and the level it
// is drawn at belongs to the channel it is being given to.
export function composeDecayTemplate (peak, halfLife, months, total) {
  const span = Math.max(2, Math.round(months))
  const mass = isFinite(total) && total > 0 ? total : 1
  let low = DECAY_RATE_LOW
  let high = DECAY_RATE_HIGH
  for (let step = 0; step < DECAY_RATE_STEPS; step++) {
    const rate = (low + high) / 2
    if (shapeHalfLife(decayShape(peak, rate, span)) < halfLife) low = rate
    else high = rate
  }
  // `high` and not the midpoint, and the difference is a tile's caption rather than a rounding.
  // Bisection lands either side of the target, and decayRange brackets what it is given by flooring
  // and ceiling onto a quarter of a month — so a half-life of exactly 2 that came out at 1.999… is
  // filed under [1.75, 2] and one that came out at 2.000… under [2, 2.25]. `high` is by the loop's
  // own invariant the end whose crossing is at or past the target, so the bracket a tile produces
  // always BEGINS at the figure the tile is captioned with.
  return decayShape(peak, high, span).map(function (weight) { return weight * mass })
}

// EVERY SHAPE A DECAY CAN BE, at this channel's own total and over its own months. One flat list —
// the peak is on each entry and the grouping is the editor's business, not this one's.
export function composeDecayTemplates (months, total) {
  const templates = []
  DECAY_PEAKS.forEach(function (peak) {
    (DECAY_HALF_LIVES[peak] || []).forEach(function (halfLife) {
      templates.push({
        key: `p${peak}h${halfLife}`,
        peak: peak,
        halfLife: halfLife,
        kernel: composeDecayTemplate(peak, halfLife, months, total)
      })
    })
  })
  return templates
}

// WHICH TILE IS LIT. There is no selection to store: the kernel is what the channel carries and a
// tile is lit when the kernel IS the one that tile would give it, which is a fact about the payload
// rather than a second piece of state that can drift out of agreement with it.
//
// `source` wins where it also matches, and that is the one ordering decision here: a channel that
// has not been touched is standing on its model's own measurement, and saying so is truer than
// naming the template that happens to coincide with it.
// A channel with NO source kernel is the case the guard is for: nothing measured this one, the tile
// that would say so is not on the screen, and a key naming a tile that is not there would light
// nothing at all. Its kernel is matched against the templates like any other, which is where the
// shape it opens on actually lives.
export function composeDecaySelected (kernel, templates, source) {
  if (!Array.isArray(kernel)) return ''
  if (Array.isArray(source) && !composeDecayMoved(kernel, source)) return DECAY_SOURCE_KEY
  const found = (templates || []).filter(function (template) {
    return !composeDecayMoved(kernel, template.kernel)
  })[0]
  return found ? found.key : DECAY_CUSTOM_KEY
}

// THE SHAPE PULLED BY HAND, AND WHY IT IS THE TWO FIGURES THAT ARE PULLED.
//
// The grid above is every combination of peak and half-life a decay is usually stated in, and it is
// where most channels are answered. What it cannot do is the figure BETWEEN two tiles, and the
// gesture people reach for in front of a bar chart is the bar itself. So the plot is draggable, and
// what a drag moves is these two figures rather than the bar it is holding: the tallest column is
// taken sideways to say WHEN the response peaks and up or down to say HOW SHARPLY it falls, and the
// whole kernel is regenerated from the pair.
//
// Which is the one thing a bar-by-bar editor could not promise. A kernel dragged column by column
// stops being a decay the moment the second column is moved — a user pulls month 4 above month 3
// and the payload carries a shape no response ever had — and it asks them to hold an arithmetic
// they have no reason to hold, the total being fixed so that every bar moved moves all the others.
// Here the shape is a decay by construction: composeDecayTemplate climbs to the peak and falls away
// from it, at the total the channel already carries, so no drag can move what a month's spend is
// worth in the end and no drag can leave a curve that is not one.

// The last month a response may peak in. DECAY_PEAKS' own end, and the platform's: decayRange reads
// this figure out of a kernel and clamps it here, so a peak dragged past it would be a picture the
// payload could not state.
export const DECAY_PEAK_MAX = 2

// And the slowest half-life, which is the same bound from the same place. The floor is not a
// constant — it belongs to the peak, a response still climbing through month one having no way to
// have delivered half of itself inside month zero — and is composeDecayHalfLifeFloor's.
export const DECAY_HALF_LIFE_MAX = 3

// What a drag moves the half-life in. A quarter of a month, which is decayRange's own grain: it
// brackets every reading onto quarters, so a shape between two steps could not be told from one of
// them in the payload. It is also what lets a drag LAND on a tile — every half-life the grid is
// filed under is a multiple of this — so pulling the bar to a shape the grid already has lights it.
export const DECAY_HALF_LIFE_STEP = 0.25

// THE FASTEST A PEAK CAN DECAY: the half-life of the shape with nothing left in the tail at all,
// which is where the drag stops going up. It is a fact about the peak rather than a choice — the
// climb to the peak is mass that has to be delivered before the peak, so peak 2 has a third of the
// response spent by the time the peak arrives and cannot reach half of it before month 2.
//
// Rounded UP onto the drag's own step so the floor is itself a place the drag can stand, with a
// tolerance so a floor that already is a multiple is not pushed to the next one.
export function composeDecayHalfLifeFloor (peak, months) {
  const span = Math.max(2, Math.round(months))
  const at = Math.max(0, Math.min(DECAY_PEAK_MAX, Math.round(Number(peak) || 0)))
  const fastest = shapeHalfLife(decayShape(at, DECAY_RATE_LOW, span))
  const stepped = Math.ceil(fastest / DECAY_HALF_LIFE_STEP - 1e-6) * DECAY_HALF_LIFE_STEP
  return Math.min(DECAY_HALF_LIFE_MAX, stepped)
}

// THE TWO FIGURES READ OFF A KERNEL, which is where a drag starts from: whatever the channel is
// carrying — its model's own measurement, a tile, or the shape of an earlier drag — is a peak and a
// half-life, and the gesture moves from there rather than from a shape nobody asked for.
//
// The peak is the tallest month, clamped into the range the payload can state; the half-life is
// shapeHalfLife's, which is decayRange's own reading. Neither is snapped: this is where the drag
// BEGINS, and a shape that jumped the moment it was touched would be answering a question the user
// has not asked yet. The snap happens on the first move, in composeDecayFromShape.
export function composeDecayShapeOf (values) {
  const kernel = (values || []).map(Number)
  const total = sum(kernel)
  if (kernel.length === 0 || !(total > 0) || kernel.some(function (value) { return !isFinite(value) })) {
    return { peak: 0, halfLife: DECAY_HALF_LIFE_MAX }
  }
  let peak = 0
  kernel.forEach(function (value, lag) { if (value > kernel[peak]) peak = lag })
  peak = Math.max(0, Math.min(DECAY_PEAK_MAX, peak))
  const half = shapeHalfLife(kernel.map(function (value) { return value / total }))
  return {
    peak: peak,
    halfLife: Math.max(
      composeDecayHalfLifeFloor(peak, kernel.length),
      Math.min(DECAY_HALF_LIFE_MAX, half)
    )
  }
}

// ONE DRAG RESOLVED: the pair the gesture arrived at, held inside what a decay can be, and the
// kernel they come to at the channel's own total and over its own months.
//
// The pair comes back as well as the curve, and that is the point of returning an object: the two
// figures the drag is actually moving are what the plot says out loud while it is being moved, and
// they have to be the figures the payload got rather than the ones the pointer was over. A peak
// dragged past month 2 and a half-life pulled above its own floor both stop, and the reading stops
// with them, which is how the wall is told from a gesture that is still travelling.
//
// Null for a kernel there is no shape to state — no months, an unreadable weight, or no mass at all
// — which leaves the channel exactly where it stood.
export function composeDecayFromShape (values, peak, halfLife) {
  const kernel = (values || []).map(Number)
  if (kernel.length === 0) return null
  if (kernel.some(function (value) { return !isFinite(value) || value < 0 })) return null
  const total = sum(kernel)
  if (!(total > 0)) return null

  const at = Math.max(0, Math.min(DECAY_PEAK_MAX, Math.round(Number(peak) || 0)))
  const floor = composeDecayHalfLifeFloor(at, kernel.length)
  const asked = Number(halfLife)
  const snapped = isFinite(asked)
    ? Math.round(asked / DECAY_HALF_LIFE_STEP) * DECAY_HALF_LIFE_STEP
    : floor
  const held = Math.max(floor, Math.min(DECAY_HALF_LIFE_MAX, snapped))
  return {
    peak: at,
    halfLife: held,
    kernel: composeDecayTemplate(at, held, kernel.length, total)
  }
}

// The two tiles that are not combinations of anything, and so are keys rather than templates. The
// channel's own is whatever the source model measured; the custom one is where a kernel that is
// none of the tiles lands — a shape dragged between two of them, a decay the source model measured
// that no template happens to coincide with, or one left behind by a template list that has since
// changed. It is a READING and not a tile anybody presses: there is no mode to enter, the drag
// being on the plot itself.
export const DECAY_SOURCE_KEY = 'source'
export const DECAY_CUSTOM_KEY = 'custom'

// Whether this channel's kernel still is the one its model measured. In the kernel's own units and
// with a tolerance, because both sides have been through a proportional rescale and a weight that
// came back to a millionth of where it started is a channel standing where it started.
export function composeDecayMoved (edit, source) {
  if (!Array.isArray(edit) || !Array.isArray(source)) return false
  const total = sum(source.map(Number))
  const grain = (total > 0 ? total : 1) * 1e-6
  if (edit.length !== source.length) return true
  return edit.some(function (value, lag) {
    return Math.abs(Number(value) - Number(source[lag])) > grain
  })
}

// The whole-model sections, generated rather than imported.
//
// The KPI row is flat: twelve equal months summing to the revenue that was typed. That is the
// deliberate reading of "total revenue" for a model with no history — there is no trend and no
// growth to carry, and the one shape a from-scratch model has is the one the user draws.
//
// The seasonality row is that drawing, and it is a percentage of each month's own revenue, so it
// rides the revenue for free and the two knobs stay independent: one says how much was sold, the
// other how unevenly across the year, and neither reaches a curve.
//
// Everything else a report would have supplied is left BLANK and not zeroed. A blank is read
// downstream as "choose a sensible value" where a zero is a claim, and the composer has no grounds
// for the claim: no measured decomposition to put a band around the co-marketing or the competition
// spend. validateEkrmForm accepts all of it — its other-spend rules are guarded on the row carrying
// data, and the "both or none filled in" rule on an attribution row is satisfied by none. The two
// spends themselves are blank too unless the Other Inputs step stated them: see writeOtherSpend.
function writeGeneratedGlobals (form, globals) {
  // Twelve equal months unless a shape says otherwise, and summing to the stated revenue either way:
  // the shape redistributes the year, it does not resize it. See COMPOSE_REVENUE_SHAPES.
  const months = composeKpiMonths(globals.revenue, globals.revenueShape)

  // One figure for both rows, which is what importGlobalCells does with the source's own: the
  // seasonality row is a percentage OF the KPI row, so the confidence in one is the confidence in
  // the other, and a row carrying values with no variation range beside it is a hard validator
  // error rather than a defaulting.
  //
  // THE PLATFORM'S OWN AND NOT ANY MODEL'S. It used to be the mean of what the models the channels
  // came from declared about their own KPI rows, which was evidence about how well four other
  // businesses were fitted and not about this one — and this row is GENERATED here, out of a revenue
  // the user typed and a shape they pressed. Nobody has measured it, so there is nothing to average:
  // what the row deserves is the default the platform states for a row nobody has history for.
  const declared = String(DEFAULT_VARIATION_RANGE)

  form.kpi.data[0] = [form.kpi.data[0][0], declared].concat(toCells(months))

  // The seasonality row is a separate statement and stays one: this says how much of each month's
  // sales is SEASONAL, where the row above says what each month sold. They are meant to agree, and
  // until the row above could be shaped at all they could not — that is the whole of what the shape
  // buys the periodic band, which derivedAttributionRows weights by the KPI row and which used to
  // collapse to the arithmetic mean of the twelve percentages because those weights were equal.
  //
  // AND IT ARRIVES IN A UNIT THIS ROW DOES NOT CARRY. The composer draws both years as percentages
  // of each month's own BASE — see SHAPE_MONTH_CAP, which is the whole reason it does — and the
  // grid, the validator and the backend read percentages of each month's own REVENUE. So the two
  // years are converted here, on the way in, and nowhere else: cloneShapeCells multiplies each
  // month by its own ratio of the one to the other. It runs AFTER the KPI row above, which is the
  // revenue half of that ratio, and after the channels, which are the other half.
  const shape = cloneShapeCells(form, globals.seasonalityShape)
  if (shape) {
    form.seasonalityAndSpotEvents.data[0] =
      [form.seasonalityAndSpotEvents.data[0][0], declared].concat(toCells(shape))
  }

  // And the row under it, which is the same kind of statement about a different kind of event: the
  // periodic row is what comes back every year, this one is what happened once. Same units — drawn
  // as a signed percentage of that month's own base and written as one of its revenue — same
  // variation range, and written the same way, which is why the two are one grid rather than two.
  //
  // Left BLANK where nobody has said anything, not zeroed, and that is the difference between this
  // row and the KPI above it. Twelve zeros are a claim that this business has no spot events; a
  // blank row is the wizard saying nobody was asked. validateEkrmForm reads both as acceptable and
  // derivedAttributionRows gives the same band for either, so what the distinction buys is a payload
  // that says only what was said to it — and a composer nobody opened the section in that emits the
  // form it emitted before this existed, cell for cell.
  const spot = cloneShapeCells(form, globals.spotShape)
  if (spot) {
    form.seasonalityAndSpotEvents.data[1] =
      [form.seasonalityAndSpotEvents.data[1][0], declared].concat(toCells(spot))
  }
}

// THE MOST THIS ROW MAY BE WRITTEN AT and still leave the five of them closing under a hundred.
//
// validateEkrmForm sums the attribution minima of every row except Base and refuses the payload at
// a hundred, and the marketing row is only one of the terms. The other four are the two that are
// blank in a composed form — co-marketing and competition — and the two that are written from the
// two years the user drew, which is where the points come from: a seasonality standing ABOVE the
// average in most months is a periodic row with a real minimum, a year of positive spot events is
// the same again on the row beneath it, and none of it moves when the marketing band does.
//
// Bisected on the centre rather than solved, because `band` is not a formula here: it widens to a
// floor of 0.12 and rounds both ends into two decimals, and the sum this has to hold under is the
// rounded one. Monotone regardless — a higher centre writes a higher minimum — so the bisection is
// the honest way to ask a rounding where its own wall is.
//
// Never taken below the least centre `band` writes a positive minimum for, because that is the OTHER
// hard error on the same row: a payload whose other rows leave no room at all is one the seasonality
// has to answer for, and writing a band of nothing would refuse it in the wrong words.
function composeBandCeiling (others) {
  const closes = function (centre) { return band(centre).min + others <= BAND_SUM_LIMIT }
  if (closes(MAX_ATTRIBUTION_SHARE)) return MAX_ATTRIBUTION_SHARE
  const least = leastAttributionCentre()
  let low = least > 0 ? least : 0
  let high = MAX_ATTRIBUTION_SHARE
  if (!closes(low)) return low
  for (let pass = 0; pass < BAND_PASSES; pass++) {
    const middle = (low + high) / 2
    if (closes(middle)) low = middle
    else high = middle
  }
  return low
}

// THE MARKETING BAND, LOWERED IF THE ROWS BESIDE IT LEAVE IT NO ROOM — and run after all four of
// them are written, which is the only moment the question can be asked.
//
// MAX_ATTRIBUTION_SHARE is 95 for exactly this reason and cannot quite say it: the row it caps is
// summed against the others, and a band written at that ceiling lands the payload at 92 plus
// whatever the periodic row came to. A year drawn above its own average puts several points there —
// writeSeasonalityBand's own band, which replaces the derived row and is therefore not knowable
// until it has run — and the sum then passes a hundred, which is a hard validator error over a row
// nobody typed, reached by typing a REVENUE.
//
// It only ever lowers, and only where the payload would otherwise be refused. The centre this
// wizard reports is untouched: writeImpliedBand returns the implied figure and every limit is read
// against that, as they were when the flat ceiling was the only clamp.
function closeMarketing (form, implied) {
  if (implied === null || !(implied > 0)) return
  const rows = form.attributions.data
  let others = 0
  for (let row = 1; row < 5; row++) {
    const value = Number((rows[row] || [])[1])
    if (isFinite(value)) others += value
  }
  const written = band(Math.min(implied, MAX_ATTRIBUTION_SHARE, composeBandCeiling(others)))
  rows[0] = [rows[0][0], String(written.min), String(written.max)]
}

// ---------------------------------------------------------------------------
// WHAT CO-MARKETING AND COMPETITION ARE CREDITED WITH
// ---------------------------------------------------------------------------
//
// Left blank, the EKRM chooses a share for either series inside the range CreateEkrm names — and it
// may choose anything there: a composed model sent a co-marketing spend of twelve thousand came back
// crediting it with three fifths of the revenue. So the Other Inputs step lets the user say it, as a
// signed share of the whole year's sales, and what is said is written as a band the builder holds
// the series to: adjust_attributions rescales a component whose fitted share falls outside its row,
// and these two are not exempt the way marketing is.
//
// Only for a series the payload carries a spend for. A share with no spend beside it is one the
// validator warns about and the builder deletes, and it is not a statement anybody could have made.

// The least a competition band is kept below nothing by, when the band band() writes for it reaches
// zero: the same three points attributionBands gives a clone's competition row.
const COMPETITION_BAND_GAP = 3
// The narrowest band either row is written at: what validateEkrmForm's attributionBandNarrow floor
// asks of a small share. band() keeps a floor of its own, but clipping it at zero can halve it.
const OTHER_BAND_FLOOR = 0.1

function otherSpendStated (form, row) {
  const cells = ((form.mediaSpend.otherSpend.data || [])[row] || []).slice(2)
  return cells.reduce(function (total, cell) { return total + (Number(cell) || 0) }, 0) > 0
}

// What the four other rows leave the co-marketing one, as the least centre band() writes for them —
// the minima of marketing, competition, periodic and spot, which is exactly what BAND_SUM_LIMIT is
// summed against. Read off the form as it stands, so it has to be asked after all four are written.
function otherRowsMinima (form) {
  const rows = form.attributions.data
  let others = 0
  ;[0, 2, 3, 4].forEach(function (row) {
    const value = Number((rows[row] || [])[1])
    if (isFinite(value)) others += value
  })
  return others
}

// The most co-marketing may be credited with on this form, as a share of sales: whatever leaves the
// six rows closing under a hundred once marketing, competition and the drawn years have had theirs.
// Marketing comes first because the curves imply it; co-marketing is what is said about what is left.
export function composeOtherAttributionCeiling (form) {
  if (!form) return MAX_ATTRIBUTION_SHARE
  return Math.max(0, Math.min(MAX_ATTRIBUTION_SHARE, composeBandCeiling(otherRowsMinima(form))))
}

// The two rows written, competition first — it is negative and so gives the co-marketing ceiling
// room rather than taking it. Returns the centres actually written, which are what the preview
// draws: a co-marketing share held at its ceiling is drawn at the ceiling, and said to have been.
function writeOtherAttributions (form, stated) {
  const said = stated || {}
  const rows = form.attributions.data
  const written = { comarketing: null, competition: null }

  const competition = Number(said.competition)
  if (said.competition !== null && said.competition !== undefined && isFinite(competition) && competition < 0 &&
      otherSpendStated(form, 1)) {
    const centre = Math.max(-MAX_ATTRIBUTION_SHARE, competition)
    const raw = band(centre)
    let min = Math.min(raw.min, 0)
    const max = Math.min(raw.max, 0)
    if (min === max) min -= COMPETITION_BAND_GAP
    if (max - min < OTHER_BAND_FLOOR) min = Math.round((max - OTHER_BAND_FLOOR) * 100) / 100
    rows[2] = [rows[2][0], String(min), String(max)]
    written.competition = centre
  }

  const comarketing = Number(said.comarketing)
  if (said.comarketing !== null && said.comarketing !== undefined && isFinite(comarketing) && comarketing > 0 &&
      otherSpendStated(form, 0)) {
    const centre = Math.min(comarketing, composeOtherAttributionCeiling(form))
    if (centre > 0) {
      const raw = band(centre)
      const min = Math.max(raw.min, 0)
      let max = Math.max(raw.max, 0)
      if (max - min < OTHER_BAND_FLOOR) max = Math.round((min + OTHER_BAND_FLOOR) * 100) / 100
      rows[1] = [rows[1][0], String(min), String(max)]
      written.comarketing = centre
    }
  }
  return written
}

// The marketing attribution band, written straight from the attribution the payload implies.
//
// The clone cannot do this and does something more elaborate: it anchors the band on the source
// model's MEASURED decomposition and moves it by the ratio the user's edits changed, which is what
// lets it promise that the correction the backend applies is the one an unedited copy would have
// got. There is no such anchor here. Composing four channels out of four models leaves no measured
// decomposition to be faithful to, so the only honest statement about how much of this revenue the
// media are credited with is the one the curves themselves make at the spend the payload carries —
// which is exactly what cloneAttribution reads.
//
// Clamped at MAX_ATTRIBUTION_SHARE, which is the same ceiling the clone's band stops at and the same
// number cloneBaseFloor is written against: the most of a revenue that marketing may be credited
// with is one figure, whichever end of the subtraction is saying it.
//
// And lowered again by closeMarketing once the rows beside it have been written, where those rows
// leave it no room to close under a hundred. It cannot be done here: the periodic row this is summed
// against is writeSeasonalityBand's, which has not run yet.
//
// Returns the centre BEFORE the clamp and before the two decimals the row rounds it to. Nothing in
// the payload needs it and the two limit readings cannot do without it: near the bottom of the range
// those two decimals are most of the number, so a limit read back off the row lands several percent
// away from itself and walks out of the corridor it exists to hold, one drag at a time.
function writeImpliedBand (form) {
  const implied = cloneAttribution(form)
  if (implied === null || !(implied > 0)) return null
  const written = band(Math.min(implied, MAX_ATTRIBUTION_SHARE))
  form.attributions.data[0] = [form.attributions.data[0][0], String(written.min), String(written.max)]
  return implied
}

// A whole EKRM form from a list of channels taken from any number of models.
//
// `channels` is the ordered entry list, one per medium the payload will carry:
//   raw          the channel's key in its own model's report
//   label        the medium name, claimed once by composerLabel when the channel was added and
//                stored, never recomputed — see there for why
//   modelId      which model it came from, for the provenance
//   read         that channel's readChannel output, cached by the caller
//
// Nothing about the model a channel came from travels here beyond that. Its size, its currency and
// what its units were worth used to, and the two figures below have taken their place.
//
// `options`:
//   statedCurves           WHERE EVERY CURVE IS PLACED, keyed by the entry's own uid:
//                          { knee, ceiling } in USD a month — the spend at which that medium stops
//                          responding, and what it generates there. The two levels the source model
//                          cannot supply, and the only thing that places a channel: a uid missing
//                          either of them is a channel this payload does not carry
//   revenue                the model's SIZE, in USD over the year. It sizes the KPI row and NOTHING
//                          else — no curve and no spend cell is a function of it, so what it moves
//                          is the marketing attribution, which is the media's contribution over it
//   revenueShape           twelve multipliers the revenue is spread across the year by, or null for
//                          twelve equal months. Renormalised here, so the row sums to `revenue`
//                          whatever comes in — see COMPOSE_REVENUE_SHAPES
//   seasonalityShape       twelve numbers, the percentage of each calendar month's revenue the
//                          seasonality accounts for
//   spotShape              the same twelve for the spot-events row, which is the other half of the
//                          same grid: what happened once rather than what comes back every year.
//                          Null and not twelve zeros for a model nobody has claimed one for — see
//                          writeGeneratedGlobals for why the row stays blank rather than zeroed
//   category, market       reference.category and reference.market
//   t                      grid-label translator, the same one createGrids takes
//   spendFactor            a multiplier on every channel's spend cells, default 1
//   spendFactors           a per-channel multiplier ON TOP of that one, keyed by the entry's own
//                          uid, for a mix solveSpendMix moved. Absent or missing a key means 1
//   spendShapes            a per-MONTH multiplier on top of both, keyed by the entry's own uid: one
//                          number per column of the spend row, for a month typed on the chart. It
//                          is not renormalised and is not meant to be — a month raised is a month
//                          that spends more, and the year's total follows it, which is the same
//                          rule the revenue row's own typed month keeps. A uid with no entry, a
//                          short array or an unreadable number is 1, so a composer nobody has typed
//                          a month into writes exactly the cells it always wrote
//   curveEdits             per-channel RATIOS over the Hill parameters the channel was fitted with,
//                          keyed by uid: { vmax, k, n }. The one thing here that moves a response
//   futureTrends           the growth each series is expected to have over the next twelve months,
//                          as a signed percentage band: { sales, media, comarketing, competition },
//                          each { min, max }. See writeFutureTrends
//   decayEdits             per-channel KERNELS, keyed by uid: one weight per lag month, in the
//                          source's own units. Absolute and not a ratio, unlike the curve above,
//                          because what the user moved IS the curve the payload carries. A channel
//                          missing a key carries the decay its source model measured, which is every
//                          channel until one is redrawn — see composeDecayKernel for what one opens
//                          on and composeDecayPayload for what it becomes on the way out
//
// The order below is not a preference. buildEkrmDefinition pairs reference.media[i] with the spend
// row i and the saturation rows 2i/2i+1 BY POSITION, so the media list has to be settled before
// syncMediaGrids lays the rows out and can never be filtered afterwards.
// THE CO-MARKETING AND COMPETITION SPEND the Other Inputs step states, each a year of twelve
// dollars or null. Written with the platform's default variation range, as the KPI row is: without
// one, a row with blank months is an error and a complete one is dropped as missing (see
// validateEkrmForm). A row that sums to nothing stays blank, since the validator refuses a row that
// carries data and totals zero. Their attributions are left blank for the model to choose.
function writeOtherSpend (form, otherSpend) {
  const series = otherSpend || {}
  ;[series.comarketing, series.competition].forEach(function (values, row) {
    if (!Array.isArray(values)) return
    const total = values.reduce(function (sum, value) { return sum + (Number(value) || 0) }, 0)
    if (!(total > 0)) return
    const label = form.mediaSpend.otherSpend.data[row][0]
    form.mediaSpend.otherSpend.data[row] = [label, String(DEFAULT_VARIATION_RANGE)].concat(toCells(values))
  })
}

// THE GROWTH EXPECTED OVER THE NEXT TWELVE MONTHS, stated on the Future Trends step as a signed
// percentage band per series. The sales and the own media spend are always written; co-marketing and
// competition only when writeOtherSpend wrote their spend, since a trend on a series with no spend is
// one the validator reports as ignored. Written as NUMBERS rather than strings: ngtorch divides the
// raw value by a hundred without checking it is one.
const FUTURE_TREND_ROWS = [
  { key: 'sales', row: 0 },
  { key: 'media', row: 1 },
  { key: 'comarketing', row: 2, spendRow: 0 },
  { key: 'competition', row: 3, spendRow: 1 }
]
function trendPercent (value) {
  const number = Number(value)
  if (!isFinite(number)) return 0
  return Math.max(-100, Math.min(100, number))
}
function writeFutureTrends (form, trends) {
  if (!trends) return
  FUTURE_TREND_ROWS.forEach(function (entry) {
    const band = trends[entry.key]
    if (!band) return
    if (entry.spendRow !== undefined && form.mediaSpend.otherSpend.data[entry.spendRow][1] === '') return
    let min = trendPercent(band.min)
    let max = trendPercent(band.max)
    if (min > max) {
      const swap = min
      min = max
      max = swap
    }
    const label = form.futureTrends.data[entry.row][0]
    form.futureTrends.data[entry.row] = [label, min, max]
  })
}

export function buildComposeForm (channels, options) {
  const opts = options || {}
  const entries = channels || []
  const spendFactor = positiveFactor(opts.spendFactor, 1)
  const mix = opts.spendFactors || {}
  const shapes = opts.spendShapes || {}
  const stated = opts.statedCurves || {}
  const curveEdits = opts.curveEdits || {}
  const decayEdits = opts.decayEdits || {}

  // WHAT IT TAKES TO LAY A CHANNEL OUT, and the two halves of it.
  //
  // The anchor is the source's: the point its own curve flattens at, which is what the curvature is
  // normalised through and which nothing the user does can move. The pair is the user's: where THIS
  // medium saturates and what it generates there, in dollars. A channel missing either is not laid
  // out at all, for the reason the clone drops a channel before syncMediaGrids rather than after —
  // a medium with no cells is rows of blanks the validator refuses, and the pairing above cannot
  // survive a gap. A channel just added is missing both, on purpose: there is nothing on the page to
  // convert its source model's own figures with, and copying them raw would read pesos as dollars.
  //
  // Note what is NOT here any more: a count. The budget used to be a share of the revenue split
  // between however many channels turned out to be buildable, so every channel's placement was a
  // function of every other channel's existence. Now each one is placed out of its own two figures,
  // and adding or removing one cannot move a single cell of any other.
  const buildable = []
  entries.forEach(function (entry) {
    const anchor = composeAnchor(entry.read)
    if (anchor === null) return
    const said = stated[entry.uid] || {}
    const knee = Number(said.knee)
    const ceiling = Number(said.ceiling)
    if (!isFinite(knee) || knee <= 0 || !isFinite(ceiling) || ceiling <= 0) return
    buildable.push({ entry: entry, anchor: anchor, knee: knee, ceiling: ceiling })
  })

  // The one division the whole import is made of, and now both of its ends are named by the user.
  // composeCells divides by the anchor and multiplies by this pair, so the source's own knee lands
  // exactly on the stated one and the response it had there lands exactly on the stated revenue.
  //
  // The knee reaches the SPEND ROW as well as the x axis, which is what opens every channel standing
  // exactly at its own saturation point — and therefore opens the mix weighted by how much money
  // each medium can absorb. That replaces the equal split, which was equal only because there was
  // nothing left to weight by; there is now, and the user stated it. spendFactor moves the whole
  // plan off that opening, and nothing but the user ever writes it; spendFactors is the per-channel
  // deviation the mix solver writes.
  const built = []
  buildable.forEach(function (item) {
    const cells = composeCells(item.entry, item.knee, item.ceiling, item.anchor)
    if (cells) built.push({ entry: item.entry, cells: cells })
  })

  const labels = built.map(function (item) { return item.entry.label })
  const grids = createGrids(opts.t || function (key) { return key }, null)
  const synced = syncMediaGrids(labels, null, COMPOSE_MONTHS)

  const form = {
    reference: {
      category: opts.category || '',
      tier: REFERENCE_TIER,
      market: opts.market || '',
      media: labels.slice()
    },
    futureTrends: grids.futureTrends,
    attributions: grids.attributions,
    seasonalityAndSpotEvents: grids.seasonalityAndSpotEvents,
    kpi: grids.kpi,
    saturation: Object.assign({}, grids.saturation, {
      data: synced.saturationData,
      mergeCells: synced.saturationMergeCells,
      disabledRows: synced.saturationDisabledRows
    }),
    decay: synced.decay,
    mediaSpend: {
      ownControllable: Object.assign({}, grids.mediaSpend.ownControllable, { data: synced.spendData }),
      otherSpend: grids.mediaSpend.otherSpend
    },
    minHalfLife: synced.minHalfLife,
    maxHalfLife: synced.maxHalfLife,
    curves: {},
    // Always null. buildEkrmOriginalDefinition passes it through so the expert wizard can name the
    // columns of a grid it reopens, and null is what a single averaged year has always sent: twelve
    // columns that are January to December by construction and have no calendar months to claim.
    months: null
  }

  built.forEach(function (item, index) {
    writeChannel(
      form,
      index,
      // Shaped on the way into the grid rather than into `built`, so what this returns is still the
      // channel's own flat year: the level, the mix and the shape are three factors over one cell,
      // and the wizard converts a typed month into the third of them by dividing by the other two.
      // A row that arrived already shaped would leave it dividing by itself.
      shapeSpendCells(item.cells, shapes[item.entry.uid]),
      spendFactor * positiveFactor(mix[item.entry.uid], 1),
      curveEdits[item.entry.uid],
      // The brackets and the kernel together, because writeChannel writes both and a channel that
      // sent one without the other would be two accounts of its own decay. Null for a kernel that
      // cannot be built from, which leaves the channel on its source's decay rather than on a broken
      // version of it.
      composeDecayPayload(decayEdits[item.entry.uid])
    )
  })

  writeGeneratedGlobals(form, {
    revenue: opts.revenue,
    revenueShape: opts.revenueShape,
    seasonalityShape: opts.seasonalityShape,
    spotShape: opts.spotShape
  })
  writeOtherSpend(form, opts.otherSpend)
  // After it, because a co-marketing or competition trend is only written for a series it wrote.
  writeFutureTrends(form, opts.futureTrends)

  // After the channels and the globals, because it reads both: the response comes off the curves and
  // the spend rows, and the revenue it is a share OF is the row just written.
  const centre = writeImpliedBand(form)
  writeDerivedAttributions(form)
  // After the derived rows and not instead of them: it needs the periodic row they leave in order to
  // overwrite it. Base is then closed against what it wrote — and unconditionally, because the
  // ordering guard in closeBase is worth having even for a form whose band could not be written.
  writeSeasonalityBand(form)
  // After the marketing and periodic rows, because co-marketing is fitted into the room they leave,
  // and before closeMarketing, which then finds nothing to take back from the curves' own share.
  const otherAttribution = writeOtherAttributions(form, opts.otherAttribution)
  // After it, and before the base is closed against the five: the marketing row is summed with the
  // periodic row writeSeasonalityBand just wrote, and a payload whose minima do not close under a
  // hundred is one validateEkrmForm refuses.
  closeMarketing(form, centre)
  closeBase(form)

  // Maximum Spend is never typed in any of these wizards: it mirrors the largest monthly spend of
  // the medium. Taken from the rows that ended up in the grid rather than from the cell
  // scaleChannelCells also returns, so this is provably the number a maxSpends watcher over the
  // grid would have written.
  maxMonthlySpends(form.mediaSpend.ownControllable.data).forEach(function (value, index) {
    form.saturation.data[2 * index][2] = value >= 0 && isFinite(value) ? String(value) : ''
  })

  return {
    form: form,
    media: built.map(function (item) {
      return { uid: item.entry.uid, raw: item.entry.raw, modelId: item.entry.modelId, label: item.entry.label }
    }),
    built: built,
    centre: centre,
    otherAttribution: otherAttribution
  }
}

// WHY THE PLAN OPENS STANDING AT THE SATURATION POINTS, and what was tried instead.
//
// Every channel's spend row opens at the figure that placed its curve — see composeCells — so the
// opening plan is every medium bought to exactly where its own card says it stops responding. It is
// the plainest reading of what the user typed and it needs no constant of ours: they said this
// medium absorbs 120k and generates 200k there, and the page opens on precisely that.
//
// What it is not is the most efficient plan. Return per dollar is highest where almost nothing is
// spent, so the obvious alternative — open at the PEAK of the plan's own ROAS curve, the Max. ROAS
// mark the trade card already draws — was tried and dropped as degenerate. It is degenerate twice
// over: a saturating response returns most per dollar at its very first dollar, and the payload's
// curve is a POLYLINE that runs straight from the origin to the first point the source model ever
// measured, so the return per dollar is not merely highest down there, it is exactly CONSTANT
// across that whole first segment. "The peak" is then "spend anything at all up to the least this
// medium was ever seen to be bought at", which is not a plan, and asking a wizard to open a media
// budget there is asking it to answer a question about size with the answer to a question about
// efficiency.
//
// So the wizard opens where it was told to and lets the user pull back. The peak is still drawn and
// still named on the chart, which is the right place for it: a level worth knowing about is not the
// same thing as a level worth being put at.

// The last point of a channel's own saturation curve, or null. A number and not an interval: the
// card compares each drawn point against it.
//
// The MEASURED span where the cells carry one: composeImport appends the fit's tail to a source
// curve, and the sparkline is a picture of what the source model saw rather than of what the payload
// sends. Falling back to the curve's last point keeps a channel whose curve was never extended —
// one whose fit was rejected — reading exactly as it did.
function sourceSpanOf (cells) {
  const measured = cells ? Number(cells.measuredSpan) : NaN
  if (isFinite(measured) && measured > 0) return measured
  const curve = cells ? cells.saturationCurve : null
  if (!curve || !Array.isArray(curve.x) || curve.x.length === 0) return null
  const last = Number(curve.x[curve.x.length - 1])
  return isFinite(last) && last > 0 ? last : null
}

// Everything the summary shows, per medium, read back off the form that was just built rather than
// re-derived — so what the user is looking at is what will be sent, and not a second opinion about
// it.
//
// The clone's counterpart takes one scope and reads every channel's decay curve out of it. There is
// no one scope here, so it rides on the entry instead, which is where readChannel already left it —
// but it is the same curve, read off the report rather than off the payload, for the reason given
// at decayCurve below.
export function composeChannelSummaries (form, built) {
  const spendRows = form.mediaSpend.ownControllable.data
  const monthlySpends = spendRows.map(function (row) { return mean(row.slice(2)) })
  const totalSpend = sum(monthlySpends)

  return (built || []).map(function (item, index) {
    const entry = item.entry
    const medium = entry.label
    const curves = form.curves[medium] || {}
    const source = curves.source || {}
    const header = form.saturation.data[2 * index] || []
    const revenue = form.saturation.data[2 * index + 1] || []
    const ranges = form.decay[medium] || { maximumEffect: [0, 0], halfLife: [0, 0] }
    const imported = item.cells.hill
    return {
      uid: entry.uid,
      medium: medium,
      channel: entry.raw,
      modelId: entry.modelId,
      modelName: entry.modelName || '',
      renamed: medium !== entry.raw,
      monthlySpend: monthlySpends[index] || 0,
      spendShare: totalSpend > 0 ? (monthlySpends[index] || 0) / totalSpend : 0,
      maximumSpend: Number(header[2]) || 0,
      saturationPoint: Number(header[3]) || 0,
      halfLife: ranges.halfLife,
      maximumEffect: ranges.maximumEffect,
      saturationCurve: source.saturation || null,
      // The curve the SOURCE model measured, which the payload's own no longer is once a lever has
      // been moved: an edit replaces form.curves[medium].source.saturation outright, so the thing an
      // edit is an edit OF has to be read off the cells. Straight off them and not re-evaluated from
      // importedHill — for a channel whose fit was rejected the two differ by exactly what fitPct
      // reports, and what the card drew before it was touched is this.
      sourceCurve: item.cells.saturationCurve || null,
      gridRevenue: revenue.slice(4).map(Number),
      // Straight off the report, and NOT the decayCurve the cells carry. That one is null for any
      // source fitted on weeks — its lag axis is in weeks and the backend reads months, so it is
      // deliberately not sent — and a card that drew nothing for every channel of a weekly model
      // would be reporting the payload's silence as the channel's. The axis beside it is labelled
      // in the source's own periods, so what is drawn is what was measured.
      decayCurve: entry.read.sourceDecay || null,
      // The pair, because the editor's sliders are anchored to the fit the channel arrived with — a
      // range read off a ceiling the user has already tripled would move out from under them — and
      // because "this curve was moved" is the difference between the two. Both are at THIS payload's
      // size: the resize is not an edit and must not show up as one.
      hill: curves.vmax !== undefined ? { vmax: curves.vmax, k: curves.k, n: curves.n } : null,
      importedHill: imported ? { vmax: imported.vmax, k: imported.k, n: imported.n } : null,
      // The last spend the channel's OWN curve reaches, as a number, which is what the card clips
      // its sparkline to. Read off the cells the channel was built from and not off form.curves: an
      // edited channel carries a tail run out to its new ceiling, which the payload needs and a
      // hundred-pixel thumbnail does not — drawn to there the shape is squeezed against the axis and
      // the one card that was edited stops being comparable with the others beside it.
      sourceSpan: sourceSpanOf(item.cells),
      mode: curves.mode || null,
      fitPct: curves.fitPct
    }
  })
}

// ---------------------------------------------------------------------------------------------
// COPYING ONE MODEL WHOLE
// ---------------------------------------------------------------------------------------------
//
// The composer's other way in, and the one that contradicts everything above: this module exists
// because there is no source model to read the whole-model sections off, and here there is one.
//
// It is still not the clone wizard. ekrmClone.js reproduces a model by IMPORTING its rows — the KPI
// cells, the seasonality cells, the co-marketing and competition spends all travel verbatim into the
// payload, and everything the user can move is an edit against that copy. This produces a set of
// ANSWERS to the questions the composer asks, and the composer then builds the same form it builds
// for a model assembled by hand. So a copy is a starting position and not a second kind of model:
// every figure it seeds is one the reader can retype on the step that owns it, and the payload that
// leaves is the composer's own, cell for cell.
//
// What that costs is the sections the composer generates rather than imports — the co-marketing and
// competition rows are not part of what it can carry, and neither is the source's own quality
// variation range. What it buys is that there is one build, one validator path and one set of walls.
//
// THE UNITS. Every level here is in the source model's own money, and an EKRM is in USD on both
// axes, so `rates` is the pair cloneUnitRates resolves: USD per unit of the spend axis and per unit
// of the response axis. A model already reporting USD on both converts by one and is asked nothing.
// Null rates mean the question has not been answered and nothing can be copied yet, which is a
// different state from a model that has nothing to offer.
export function composeCopyPlan (entry, rates) {
  const scope = entry ? entry.scope : null
  const imported = entry ? entry.imported : null
  if (!scope || !imported || !rates) return null
  const spendRate = Number(rates.spend)
  const revenueRate = Number(rates.revenue)
  if (!isFinite(spendRate) || spendRate <= 0) return null
  if (!isFinite(revenueRate) || revenueRate <= 0) return null

  const channels = []
  const skipped = (imported.skipped || []).slice()
  const offered = imported.kept || []
  offered.forEach(function (source) {
    const anchor = composeAnchor(source.read)
    // composeImport has already refused a channel with no saturation point and no curve, so what is
    // left for this to catch is a curve the anchor cannot be solved through. It is the
    // same silence: without the point the placement is normalised through there is no pair to state
    // and nothing to state it about.
    if (anchor === null) {
      skipped.push({ raw: source.raw, reason: SKIP_NO_SATURATION })
      return
    }
    const knee = anchor.spend * spendRate
    const ceiling = anchor.response * revenueRate
    // The two figures a card is placed by, and the floor every figure in this wizard clears. A pair
    // that rounds away is not a small channel, it is a channel the build refuses — see COMPOSE_FLOOR
    // — and a copy must not seed a card with one.
    if (!(knee >= COMPOSE_FLOOR) || !(ceiling >= COMPOSE_FLOOR)) {
      skipped.push({ raw: source.raw, reason: SKIP_NO_SATURATION })
      return
    }
    channels.push({
      raw: source.raw,
      knee: knee,
      ceiling: ceiling,
      // THE YEAR THIS MEDIUM WAS BOUGHT OVER, as the multipliers the composer holds a spend year in.
      //
      // A card opens buying twelve copies of its own saturation point, and this is the only place
      // the source's own calendar can land: the ratio of what that business spent in a month to the
      // point the curve is placed through. Both are in the SOURCE's units and the division cancels
      // them, so this is the one part of a copy that needs no rate at all — and it stays true when
      // the pair above is retyped, the shape being a multiplier over whatever the card now states.
      //
      // A medium the source stopped buying carries its twelve zeros like any other year: its curve,
      // its knee and its decay are as measured as any other channel's, and the payload states a
      // medium with no spend without complaint.
      spendShape: monthShape(source.read.sourceSpend, anchor.spend)
    })
  })
  if (channels.length === 0) return null

  const kpi = kpiMonthly(scope, COMPOSE_MODE, null)
  const revenue = sum(kpi) * revenueRate
  return {
    channels: channels,
    // The size of the business, which the composer asks for outright and which no reading of the
    // media may seed — except this one, which is not a reading of the media: it is what the model
    // being copied sold.
    revenue: isFinite(revenue) && revenue > 0 ? revenue : null,
    // And how it fell across the year, as multipliers of mean one. Scale-free, so the rate has
    // already cancelled out of it.
    revenueShape: composeRevenueShapeOf(kpi),
    // The source's own seasonality, in the unit the PAYLOAD carries it in — a percentage of each
    // month's own revenue. The composer draws it as a percentage of each month's own base, and the
    // two differ by a ratio that cannot be known until the form has been built, so the conversion
    // is composeShareOfBase's and happens after the channels and the revenue are in.
    seasonality: cloneSeasonalityPattern(scope),
    skipped: skipped
  }
}

// Twelve months read as multipliers of one figure. Empty for anything that is not twelve months or
// a positive divisor, which is the caller's cue to leave the row flat.
function monthShape (values, divisor) {
  if (!Array.isArray(values) || values.length !== COMPOSE_MONTHS) return null
  if (!isFinite(divisor) || divisor <= 0) return null
  return values.map(function (value) {
    const number = Number(value)
    return isFinite(number) && number > 0 ? number / divisor : 0
  })
}

// A YEAR STATED IN THE PAYLOAD'S UNIT, READ BACK IN THE COMPOSER'S — the inverse of cloneShapeCells,
// and the one conversion a copy needs that the composer had no use for before:
//
//     share_of_base = share_of_revenue · R/B
//
// It is the direction that can overflow. B < R wherever the curves return anything, so the ratio is
// above one and a source model's modest seasonality can land past the cap the composer holds its own
// drawn years to. Every month that does is held at the cap and NAMED — `held` is what lets the
// wizard say which months it could not carry rather than showing a year quietly flattened into one
// it never drew.
//
// The cap is the whole row's, not this row's: it is read against the sum of the two drawn years, and
// a copy seeds the periodic one alone, so `other` is the spot year where a caller has one.
//
// Null wherever cloneBaseMonthly is — a form with no readable base — which is the caller's cue to
// seed nothing and leave the section as the composer opens it.
export function composeShareOfBase (form, year, other) {
  const drawn = seasonalityYear(year)
  const base = cloneBaseMonthly(form)
  if (!drawn || !base) return null
  const kpi = ((form.kpi.data || [])[0] || []).slice(2).map(Number)
  const neighbour = seasonalityYear(other)
  const held = []
  const shares = base.map(function (money, month) {
    const revenue = Number(kpi[month])
    const share = Number(drawn[month % 12])
    if (!isFinite(revenue) || !(revenue > 0) || !isFinite(share) || !(money > 0)) return 0
    const beside = neighbour ? Math.abs(Number(neighbour[month % 12]) || 0) : 0
    const cap = Math.max(0, SHAPE_MONTH_CAP - beside)
    const wanted = Math.round(100 * share * revenue / money) / 100
    const value = Math.max(-cap, Math.min(cap, wanted))
    if (Math.abs(value - wanted) > 0.005) held.push(month)
    return value
  })
  return { year: shares, held: held }
}

// ---------------------------------------------------------------------------
// A YEAR TAKEN OFF ANOTHER MODEL'S SALES
// ---------------------------------------------------------------------------
//
// The composer's fourth step asks for one figure and a shape: what the business sold, and how that
// sold year fell across twelve months. The figure is nobody's but the user's; the shape is a fact
// about a business, and there are models on this platform that have measured one.
//
// So it can be borrowed, and borrowing it needs none of the machinery a copy needs. What travels is
// twelve multipliers of mean one — the row is renormalised on the way in, so the total the user
// typed stands and only the columns move — and a multiplier is dimensionless. There is no unit to
// convert, no rate to ask for and no curve to fit: every model with a finished report can be read,
// whatever it counts its money or its sales in, which is exactly what the copy on step 1 cannot do.

// The years a shape may be taken from: the model's own complete calendar years, most recent last.
//
// Complete and nothing else, in both senses this file cares about. A month the KPI series does not
// cover reads as zero, and a January that is really three weeks of January is a January that sold
// less — either one is a dent in the shape that says something about the data rather than about the
// business. completeCalendarYears already answers exactly this question for the clone's period
// picker, down to walking back only as far as the years stay consecutive, which is the same run the
// EKRM would read as a history.
export function composeShapeYears (scope) {
  const fit = scope && scope.v2 && scope.v2.model_fit && scope.v2.model_fit.Real
  return completeCalendarYears(fit)
}

// THAT YEAR, AS THE COMPOSER HOLDS ONE: twelve multipliers of mean one.
//
// `year` names one of composeShapeYears, or is null for the average of all of them. The average is
// taken over the complete years alone and NOT through CALENDAR_AVERAGE, which is the whole reason
// this does its own arithmetic: that mode averages every usable month of the history, so a model
// whose last year stops in August would have those eight months averaged over one more year than
// the other four and come back with a step in it that nothing in the business put there.
//
// Read by explicit month window either way, so a year is the twelve months of that year and an
// average is those twelve months of each year in turn, divided by how many years there were.
//
// Null for a model with no complete year, and null again for a row composeRevenueShapeOf will not
// take — a year that sold nothing has no shape, and a wizard must not draw one for it.
export function composeSalesShape (scope, year) {
  const years = composeShapeYears(scope)
  if (years.length === 0) return null
  const wanted = years.indexOf(Number(year)) === -1 ? years : [Number(year)]
  const totals = []
  for (let month = 0; month < COMPOSE_MONTHS; month++) totals.push(0)
  wanted.forEach(function (one) {
    const months = kpiMonthly(scope, COMPOSE_MODE, monthsForYears([one]))
    for (let month = 0; month < COMPOSE_MONTHS; month++) {
      const value = Number(months[month])
      totals[month] += isFinite(value) ? value : 0
    }
  })
  return composeRevenueShapeOf(totals.map(function (total) { return total / wanted.length }))
}

// THE SEASON THAT SAME MODEL SELLS IN, over the same years and read the same way: twelve
// percentages of each month's own revenue, which is the unit the PAYLOAD carries a seasonality in
// and the one cloneSeasonalityPattern already answers in. What the composer draws is a percentage
// of each month's BASE, and the two differ by a ratio that cannot be known until a form has been
// built — so the conversion stays composeShareOfBase's, exactly as it is for a copy.
//
// A year here rather than the whole history, for the reason composeSalesShape has and one of its
// own: a percentage of revenue is a ratio of two series, and averaging it over months that are half
// covered divides one dent by another. Every year is read whole and the percentages are averaged
// across the years, which is the same order cloneSeasonalityPattern's own average is taken in.
//
// Null for a model that carries no seasonality series, and null again for one whose season is
// twelve zeros — a business with no season has nothing to lend, and a row of zeros lent as if it
// were a statement would flatten a year the reader had already drawn.
export function composeSeasonShape (scope, year) {
  const years = composeShapeYears(scope)
  if (years.length === 0) return null
  const wanted = years.indexOf(Number(year)) === -1 ? years : [Number(year)]
  const totals = []
  for (let month = 0; month < COMPOSE_MONTHS; month++) totals.push(0)
  let read = 0
  wanted.forEach(function (one) {
    const months = monthsForYears([one])
    const vector = seasonalityVector(scope, kpiMonthly(scope, COMPOSE_MODE, months), COMPOSE_MODE, months)
    if (!vector) return
    read += 1
    for (let month = 0; month < COMPOSE_MONTHS; month++) {
      const value = Number(vector[month])
      totals[month] += isFinite(value) ? value : 0
    }
  })
  if (read === 0) return null
  const season = seasonalityYear(totals.map(function (total) { return total / read }))
  if (!season) return null
  return season.some(function (value) { return value !== 0 }) ? season : null
}

// ---------------------------------------------------------------------------
// A YEAR TAKEN OFF ANOTHER MODEL'S INPUTS
// ---------------------------------------------------------------------------
//
// The other-inputs step asks for the brand's spend outside the category and the competition's, and
// a year of either is a shape the way a year of sales is: the total is the user's, how it falls
// across the months is something a model on this platform may have seen. What it has seen is its
// own inputs — the media it spent on, and the external series the modeller fed in beside them — so
// those are what may be read, each one on its own or summed.
//
// Every entry is a { name: {x, y} } map, which is the shape monthlyVectorTotal sums, so a channel,
// all of the media, a variable and one of its categories are read by the same arithmetic.

// The series a model offers, media first. `key` is what the selector holds and what the payload
// records; `name` and `category` are the raw report names, left to the caller to display.
export function composeInputSeriesItems (scope) {
  const items = []
  const media = spendChannels(scope)
  const channels = Object.keys(media).sort()
  if (channels.length > 0) {
    items.push({ key: 'media_total', group: 'media', name: null, category: null, series: media })
    channels.forEach(function (channel) {
      items.push({ key: 'media:' + channel, group: 'media', name: channel, category: null, series: { [channel]: media[channel] } })
    })
  }
  const inputs = scope && scope.v2 && scope.v2.inputs_over_time
  if (inputs && typeof inputs === 'object') {
    Object.keys(inputs).forEach(function (variable) {
      const categories = inputs[variable] && inputs[variable].categories
      if (!categories || typeof categories !== 'object') return
      const names = Object.keys(categories)
      if (names.length === 0) return
      items.push({ key: 'external:' + variable, group: 'external', name: variable, category: null, series: categories })
      // A variable of one category is that category, and listing it twice is the same row twice.
      if (names.length < 2) return
      names.forEach(function (category) {
        items.push({
          key: 'external:' + variable + ':' + category,
          group: 'external',
          name: variable,
          category: category,
          series: { [category]: categories[category] }
        })
      })
    })
  }
  return items
}

// THAT SERIES AS TWELVE MULTIPLIERS OF MEAN ONE, over the same complete years composeSalesShape
// reads and averaged the same way — the KPI's years, because they are the window the report was
// fitted on and every input of it covers them.
//
// Null for a model with no complete year, and null for a row composeRevenueShapeOf will not take: a
// series that spent nothing has no shape, and one that goes below zero — a temperature, an index —
// is not a spend and cannot be read as one.
export function composeInputShape (scope, series, year) {
  if (!series) return null
  const years = composeShapeYears(scope)
  if (years.length === 0) return null
  const wanted = years.indexOf(Number(year)) === -1 ? years : [Number(year)]
  const totals = []
  for (let month = 0; month < COMPOSE_MONTHS; month++) totals.push(0)
  wanted.forEach(function (one) {
    const months = monthlyVectorTotal(series, COMPOSE_MODE, monthsForYears([one]))
    for (let month = 0; month < COMPOSE_MONTHS; month++) {
      const value = Number(months[month])
      totals[month] += isFinite(value) ? value : 0
    }
  })
  return composeRevenueShapeOf(totals.map(function (total) { return total / wanted.length }))
}
